// The client application: the ui-flow §2 state machine, wiring net ↔ store ↔ render ↔ ui
// (architecture §9).
//
// Main loop (architecture §9.1), once per requestAnimationFrame(now):
//  1 skip the frame if the menu is open and < 33 ms since the last drawn frame (30 fps cap);
//  2 rt = clock.renderTick(now);  3 store.sample(rt);
//  4 camera.update(dt, store.view.own, store.observerCamera, W, H);
//  5 inputSender.update(now, camera, mouse.x, mouse.y);
//  6 renderer.draw(camera, store, settings, quality, now);
//  7 HUD: score every frame (DOM only on change), leaderboard/minimap when their feed version
//    changed;  8 quality sample (Auto graphics, M2).
//
// Implementer: client UI/app. `transition` (below) is complete and is the single source of the
// state rules; `App` owns the side effects (showing screens, sending PLAY, dialogs).
//
// Client event bus (events/bus.ts): `app.bus` carries every gameplay, money and UI event. The
// app wires the producers — `attachHandlers(…, bus)` (server messages and sent intents),
// `RenderEventTap.afterSample` after `store.sample(rt)` (render-time eat/merge/pop/death/first
// cell), and its own `appState`, `uiClick` (via `playClick(source)`, the dep every menu
// component gets) and `moneyFlow`. Audio, HUD, onboarding and render subscribe to `app.bus`.
//
// Money rooms (money-mode §2, §4, §7; owner: money.enabled = false in production) — the pieces
// for the money UI implementer:
// - `moneyTransition` (pure, below) is the money flow: off → stakeSelect → joining → ready →
//   buying → spawning → inLife → (death / cash-out) ready …; `app.money` is its signal.
// - `openMoney()` (menu "Play for money": fetches /api/info.money), `pickStake(usd)` (finds and
//   joins the stake's room), Play while `ready` (dev buy-in via POST /api/money/ticket, then PLAY
//   with the ticket), `leaveMoney()` (back to the free game), `setCashOutHeld(held)` (Q / the
//   touch hold button: CASH_OUT_START / CANCEL).
// - APP_STATE.CASHED_OUT is the cash-out screen after CASH_OUT_DONE (`cashOutScreen`,
//   ui/modals/cashout.ts).
// - Onboarding (owner exception (c), ui-qol §9.1): `onboarding` is the controls hint
//   (ui/onboarding/controls-hint.ts, plugged in by start()); the app feeds it from the bus.
//
// UI timing (ui-qol §3, visuals §11): `setDim` eases the arena dim (the renderer's `setDim` when
// it has one, else the DOM dim layer); Esc reveals the menu after 1.03 s, a death with "Skip round
// summary" at +2.95 s (`#menu[data-reveal]`); Play hides the menu at once (`is-launching`).

import {
  AMOUNT_KIND,
  type AmountKind,
  ARENA_CLIENT,
  ARENA_EVENT,
  cameraZoom,
  type CashOutDoneMessage,
  encodeArenaInput,
  type FrameMessage,
  type DeathMessage,
  type ErrorMessage,
  type MatchResultMessage,
  MODE,
  type ModeId,
  modeIdFromCode,
  lamportsToUsd,
  MONEY_DEFAULTS,
  NET_TIMING,
  S2C,
  NOTICE,
  type NoticeMessage,
  parseLocationHash,
  type PartyMessage,
  PUBLIC_MODE_TILES,
  type Rgb,
  type WelcomeMessage,
  WHEEL_ZOOM,
} from '@ballio/shared';
import { ArenaBackdrop } from './arena/backdrop.js';
import { ArenaClient } from './arena/client.js';
import { DemoArena } from './arena/demo.js';
import { arenaResultView, compactUsd, cssEase, resultsDelayMs } from './arena/hud-model.js';
import { ArenaResults } from './arena/results.js';
import { AssetLoader } from './assets/loader.js';
import {
  type AssetManifest,
  EMPTY_MANIFEST,
  fetchManifest,
  nameSkinFor,
} from './assets/manifest.js';
import { loadDropIns } from './audio/dropins.js';
import type { AuthUser } from './auth/auth.js';
import { AccountSession } from './auth/session.js';
import { Sfx } from './audio/sfx.js';
import { attachAudioTriggers } from './audio/triggers.js';
import {
  type ClientBus,
  createClientBus,
  type MoneyErrorKind,
  moneyErrorKind as moneyErrorKindOf,
  type UiClickSource,
} from './events/bus.js';
import { RenderEventTap } from './events/render-tap.js';
import { Camera } from './game/camera.js';
import { TickClock } from './game/clock.js';
import {
  formatLeaderboardAmount,
  formatMoney,
  resolveLabelCurrency,
  WorldStore,
} from './game/world-store.js';
import { InputSender } from './input/input-sender.js';
import { type KeyAction, KeyboardInput } from './input/keyboard.js';
import { GamepadInput } from './input/gamepad.js';
import { MouseInput, wheelNotches } from './input/mouse.js';
import { SteerController } from './input/steer.js';
import { TouchInput } from './input/touch.js';
import {
  type ApiInfoResponse,
  CONNECTOR_PHASE,
  fullRetryDelayMs,
  type ConnectTarget,
  Connector,
  fetchInfo,
  type GiveUpReason,
} from './net/discovery.js';
import { attachHandlers, type GameSink } from './net/handlers.js';
import {
  type ApiMoneyInfo,
  type ApiMoneyStatus,
  type ApiMoneyTicket,
  fetchMoneyInfo,
  fetchMoneyStatus,
  findMoneyRoom,
  MoneyHttpError,
  type MoneyTicketError,
  requestDevTicket,
} from './net/money-api.js';
import { GameSocket } from './net/socket.js';
import {
  type QualityLevel,
  qualityProfile,
  type RenderQuality,
  Renderer,
} from './render/renderer.js';
import { TextCache } from './render/text-cache.js';
import { DimOverlay } from './render/overlay.js';
import { type Ctx2D, get2d } from './render/canvas.js';
import { css } from './render/theme.js';
import {
  clearMoneyTicket,
  isOnboardingSeen,
  type ArenaRoomKind,
  loadArenaBest,
  loadMoneyTicket,
  loadString,
  saveArenaBest,
  markOnboardingSeen,
  type QualitySetting,
  resolveReduceMotion,
  saveMoneyStake,
  saveMoneyTicket,
  saveString,
  type Settings,
  SettingsStore,
  STORAGE_KEYS,
} from './settings/store.js';
import { applyFavicon } from './ui/brand.js';
import { type ReadonlySignal, type Signal, signal, type Unsubscribe } from './ui/dom.js';
import { type BannersHud, createBanners } from './ui/hud/banners.js';
import { createDimLayer, DIM, type DimLayer, DimRamp, hudDimFilter } from './ui/hud/dim.js';
import {
  createLeaderboard,
  displayName,
  type LeaderboardHud,
  type LeaderboardOptions,
} from './ui/hud/leaderboard.js';
import { createMoneyHud, type MoneyHud } from './ui/hud/money.js';
import { createScore, type ScoreHud } from './ui/hud/score.js';
import { createSpectateBar, type SpectateBar } from './ui/hud/spectate-bar.js';
import { createStatsReadout, type StatsHud } from './ui/hud/stats.js';
import { type I18nKey, t } from './ui/i18n/t.js';
import { banLine, isBanned } from './ui/menu/account.js';
import { createHome, type HomeScreen } from './ui/menu/home.js';
import { createSkinButton } from './ui/menu/skin-picker.js';
import { createTerritoryHome, type TerritoryHome } from './ui/menu/territory-home.js';
import { createMenuLayout, type MenuLayout } from './ui/menu/layout.js';
import {
  buyInText,
  createMoneyCard,
  formatStake,
  type MoneyCard,
  moneyErrorText,
} from './ui/menu/money.js';
import { createModesCard, type ModesCard } from './ui/menu/modes.js';
import { finalNick } from './ui/menu/nick.js';
import { createCashOutScreen } from './ui/modals/cashout.js';
import {
  closeDialog,
  confirmDialog,
  type DialogId,
  type DialogOptions,
  messageDialog,
  setDialogClick,
  showDialog,
} from './ui/modals/dialogs.js';
import { openHelp } from './ui/modals/help.js';
import { closeAllModals, closeTopModal, hasOpenModal, initModals } from './ui/modals/modal.js';
import { openSettings as openSettingsModal } from './ui/modals/settings.js';
import { createSummary, type PracticeResults, type SummaryScreen } from './ui/modals/summary.js';
import { createPracticeCard, type PracticeCard } from './ui/menu/practice.js';
import { openUsernameDialog } from './ui/modals/username.js';
import { type ControlsHint, createControlsHint } from './ui/onboarding/controls-hint.js';
import { clearToasts, initToasts, noticeBox, TOAST_MS, toast } from './ui/toasts.js';

// ---------------------------------------------------------------------------------------------
// State machine (ui-flow §2)
// ---------------------------------------------------------------------------------------------

export const APP_STATE = {
  BOOT: 'boot',
  HOME: 'home',
  PLAYING: 'playing',
  SUMMARY: 'summary',
  SPECTATING: 'spectating',
  /**
   * Money rooms: the cash-out screen after CASH_OUT_DONE (money-mode §4, §7: the death-screen
   * stats plus the value received in USD and SOL). The player is an observer now.
   */
  CASHED_OUT: 'cashedOut',
} as const;
export type AppState = (typeof APP_STATE)[keyof typeof APP_STATE];

export type AppEvent =
  /** The first FRAME of the socket arrived (boot → home). */
  | { readonly type: 'firstFrame' }
  /** SPAWNED and the following FRAME with OWN arrived (after Play, or a queued BR spawn). */
  | { readonly type: 'playing' }
  /** Play/Enter while alive on the Esc menu: hide the menu, no respawn. */
  | { readonly type: 'resume' }
  /** Esc: playing → home (Esc menu); spectating → home; summary → home (= Continue). */
  | { readonly type: 'escape' }
  /** DEATH arrived. `mode` = the room's mode; `skipSummary` = the setting. */
  | { readonly type: 'death'; readonly mode: ModeId; readonly skipSummary: boolean }
  /** Continue on the round summary or the cash-out screen. */
  | { readonly type: 'continue' }
  /** CASH_OUT_DONE arrived (money rooms): playing (or the Esc menu) → the cash-out screen. */
  | { readonly type: 'cashOutDone' }
  /** Settings ▸ Watch a game, or Watch in the BR results (sent SPECTATE). */
  | { readonly type: 'watch' }
  /** The socket closed unexpectedly while alive, 1 s ago (the app shows connection_lost). */
  | { readonly type: 'connectionLost' }
  /** cant_reach ▸ Retry (restarts boot). */
  | { readonly type: 'retry' };

/**
 * The next state (ui-flow §2). Events that do not apply leave the state unchanged.
 * - Death while `home` is showing (Esc menu open) stays on `home` with no summary.
 * - Death by mode: FFA, Party, Experimental, Teams (and Rush) → `summary`, or `home` with Skip
 *   round summary; Battle Royale → `home` (results modal on top); Team Rush → stays `playing`
 *   (respawn overlay).
 * - A cash-out (money rooms) always shows the cash-out screen, also from the Esc menu and with
 *   Skip round summary on (the amount received must be seen); Continue / Esc → `home`.
 */
export function transition(state: AppState, ev: AppEvent): AppState {
  switch (ev.type) {
    case 'firstFrame':
      return state === APP_STATE.BOOT ? APP_STATE.HOME : state;
    case 'playing':
      return state === APP_STATE.HOME || state === APP_STATE.SPECTATING ? APP_STATE.PLAYING : state;
    case 'resume':
      return state === APP_STATE.HOME ? APP_STATE.PLAYING : state;
    case 'escape':
      return state === APP_STATE.PLAYING ||
        state === APP_STATE.SPECTATING ||
        state === APP_STATE.SUMMARY ||
        state === APP_STATE.CASHED_OUT
        ? APP_STATE.HOME
        : state;
    case 'death':
      if (state !== APP_STATE.PLAYING) return state;
      if (ev.mode === 'teamrush') return APP_STATE.PLAYING;
      if (ev.mode === 'battleroyale') return APP_STATE.HOME;
      return ev.skipSummary ? APP_STATE.HOME : APP_STATE.SUMMARY;
    case 'continue':
      return state === APP_STATE.SUMMARY || state === APP_STATE.CASHED_OUT ? APP_STATE.HOME : state;
    case 'cashOutDone':
      return state === APP_STATE.PLAYING || state === APP_STATE.HOME ? APP_STATE.CASHED_OUT : state;
    case 'watch':
      return state === APP_STATE.HOME ? APP_STATE.SPECTATING : state;
    case 'connectionLost':
      return state === APP_STATE.PLAYING || state === APP_STATE.SPECTATING ? APP_STATE.HOME : state;
    case 'retry':
      return APP_STATE.BOOT;
  }
}

// ---------------------------------------------------------------------------------------------
// Money flow (money-mode §1.2, §2.1, §4): stake select → join → buy-in → play → cash-out
// ---------------------------------------------------------------------------------------------

/**
 * Steps of the money flow. The menu (home) shows:
 * - `off`: the free game (default); the money entry point is a menu button → `openMoney()`.
 * - `stakeSelect`: the stake list from /api/info.money (`app.moneyInfo`): per stake the humans in
 *   the room you would join (`pools[].joinHumans` / `maxPlayers`, shown BEFORE the buy-in) and
 *   full pools disabled → `pickStake(usd)`.
 * - `joining`: finding and connecting to the stake's room (connecting blocker).
 * - `ready`: in the money room (menu open over its live arena); Play reads "Play ${stake}" →
 *   `onPlayPressed` buys in.
 * - `buying`: POST /api/money/ticket in flight (spinner; the button stays disabled).
 * - `spawning`: PLAY with the ticket sent, waiting for SPAWNED.
 * - `inLife`: a money life is on screen (HUD: own value, cash-out ring, hold-Q hint).
 * - `error`: `app.moneyError` says why (buy-in error, ERROR 11–16, room full, disconnected);
 *   Back → `stakeSelect`, Leave → `off`.
 * After a death or a cash-out the flow returns to `ready` (a new life needs a new buy-in; no
 * top-up, money-mode §2.1 rule 4).
 */
export const MONEY_FLOW = {
  OFF: 'off',
  STAKE_SELECT: 'stakeSelect',
  JOINING: 'joining',
  READY: 'ready',
  BUYING: 'buying',
  SPAWNING: 'spawning',
  IN_LIFE: 'inLife',
  ERROR: 'error',
} as const;
export type MoneyFlowStep = (typeof MONEY_FLOW)[keyof typeof MONEY_FLOW];

export type MoneyFlowEvent =
  /** "Play for money" on the menu. */
  | { readonly type: 'open' }
  /** A stake was picked: find and join its room. */
  | { readonly type: 'pickStake' }
  /** The money room's WELCOME + first FRAME arrived. */
  | { readonly type: 'joined' }
  /** Play pressed in the money room: buy in. */
  | { readonly type: 'buy' }
  /** PLAY with a ticket was sent (a fresh one, or the unused one kept after a refusal). */
  | { readonly type: 'ticketSent' }
  /** SPAWNED + OWN: the money life is on screen. */
  | { readonly type: 'spawned' }
  /** DEATH or CASH_OUT_DONE: back to the room's menu (the next life needs a new buy-in). */
  | { readonly type: 'lifeEnded' }
  /** Any failure (buy-in, ERROR 11–16, find, disconnect). */
  | { readonly type: 'failed' }
  /** Back to the stake list (from ready or error). */
  | { readonly type: 'back' }
  /** Leave money mode (back to the free game). */
  | { readonly type: 'leave' }
  /** The money room's socket dropped; the Connector is reconnecting to the stake's arena. */
  | { readonly type: 'rejoin' };

/** The next money-flow step. Events that do not apply leave the step unchanged. */
export function moneyTransition(step: MoneyFlowStep, ev: MoneyFlowEvent): MoneyFlowStep {
  const F = MONEY_FLOW;
  switch (ev.type) {
    case 'open':
      return step === F.OFF || step === F.ERROR ? F.STAKE_SELECT : step;
    case 'pickStake':
      return step === F.STAKE_SELECT || step === F.READY ? F.JOINING : step;
    case 'joined':
      return step === F.JOINING ? F.READY : step;
    case 'buy':
      return step === F.READY ? F.BUYING : step;
    case 'ticketSent':
      return step === F.BUYING || step === F.READY || step === F.ERROR ? F.SPAWNING : step;
    case 'spawned':
      return step === F.SPAWNING ? F.IN_LIFE : step;
    case 'lifeEnded':
      return step === F.IN_LIFE || step === F.SPAWNING ? F.READY : step;
    case 'failed':
      return step === F.OFF ? step : F.ERROR;
    case 'back':
      return step === F.READY || step === F.ERROR ? F.STAKE_SELECT : step;
    case 'leave':
      return F.OFF;
    case 'rejoin':
      return step === F.OFF || step === F.STAKE_SELECT ? step : F.JOINING;
  }
}

/** Why the money flow is in `error`. */
export type MoneyFlowError =
  | { readonly kind: 'buyIn'; readonly error: MoneyTicketError }
  | { readonly kind: 'server'; readonly error: MoneyErrorKind }
  | { readonly kind: 'disabled' }
  /**
   * Every arena of the stake is full (or the server's capacity guard refused). With `retryAtMs`
   * (performance.now() clock) the app asks again then by itself: the card shows the countdown,
   * not an error (architecture §6.3).
   */
  | { readonly kind: 'full'; readonly retryAtMs?: number }
  | { readonly kind: 'network' }
  | { readonly kind: 'disconnected' }
  /** Not signed in (money needs an account). */
  | { readonly kind: 'signIn' }
  /** The server refused the sign-in (token refused twice): signed out, sign in again. */
  | { readonly kind: 'auth' }
  /** Signed in without a username. */
  | { readonly kind: 'username' };

/** What the money UI reads (the `app.money` signal). */
export interface MoneyFlowState {
  readonly step: MoneyFlowStep;
  /** The stake picked (USD), or null. */
  readonly stakeUsd: number | null;
  /** /api/info.money from the last `openMoney()`, or null. */
  readonly info: ApiMoneyInfo | null;
  /** Set while `step === 'error'`. */
  readonly error: MoneyFlowError | null;
  /** Humans in the joined room's pool when it was found (shown before the buy-in). */
  readonly roomHumans: number | null;
  /**
   * The account's earlier money life still in an arena, frozen (a dropped socket, money-mode §5),
   * from /api/money/status: its value (lamports) and when it cashes out (local ms). Null otherwise.
   */
  readonly frozenLife: { readonly value: number; readonly cashOutAtMs: number } | null;
}

const MONEY_IDLE: MoneyFlowState = {
  step: MONEY_FLOW.OFF,
  stakeUsd: null,
  info: null,
  error: null,
  roomHumans: null,
  frozenLife: null,
};

/** The longest a DEATH waits for its last own cell's eat to be drawn (`onDeath`). */
export const DEATH_DRAW_WAIT_MS = 300;

/** /api/money/status poll while an earlier money life of this account is still running. */
export const MONEY_STATUS_POLL_MS = 2000;

// ---------------------------------------------------------------------------------------------
// Practice flow (money-mode §1.4): the free arena with the money room's rules and play money
// ---------------------------------------------------------------------------------------------

/**
 * Steps of the practice flow: `off` (not in practice) → `joining` (the Connector's practice
 * target is finding and connecting) → `ready` (in the practice room; Play sends PLAY without a
 * ticket: the server issues a free one) ↔ lives; `error` when the server has no practice arena.
 * No sign-in, no buy-in. Leaving goes back to the free game.
 */
export const PRACTICE_FLOW = {
  OFF: 'off',
  JOINING: 'joining',
  READY: 'ready',
  ERROR: 'error',
} as const;
export type PracticeFlowStep = (typeof PRACTICE_FLOW)[keyof typeof PRACTICE_FLOW];

export interface PracticeFlowState {
  readonly step: PracticeFlowStep;
}

export const PRACTICE_IDLE: PracticeFlowState = Object.freeze({ step: PRACTICE_FLOW.OFF });

// ---------------------------------------------------------------------------------------------
// Hooks for UI lanes (implemented elsewhere, plugged in by the app)
// ---------------------------------------------------------------------------------------------

/**
 * The first-play controls hint (ui-qol §9.1; owner exception (c)). Implementer: U2
 * (`ui/onboarding/controls-hint.ts`); U1 assigns it to `app.onboarding` in `start()`. The app
 * calls these from bus events; the component owns timing, the ✓ rows, storage
 * (`markOnboardingSeen`) and its DOM (mount it in the HUD layer).
 */
export interface OnboardingHint {
  /**
   * The first own cell of a life was drawn (`firstOwnCellDrawn`). `touch`: show the touch rows.
   * `money`: add the "Hold {key} to cash out" row (`cashOutKey` from WELCOME). The hint decides
   * itself whether to show (`isOnboardingSeen('controls' | 'money')`); it appears 600 ms later.
   */
  onFirstOwnCell(info: {
    readonly touch: boolean;
    readonly money: boolean;
    readonly cashOutKey: string;
  }): void;
  /** Progress: a split / eject / cash-out start was sent; 'move' is tracked by the hint itself. */
  onAction(action: 'split' | 'eject' | 'cashOut'): void;
  /** Death, Esc or leaving play: fade out now. */
  dismiss(): void;
}

/** What the cash-out screen shows besides the life stats (money-mode §7). */
export interface CashOutView {
  /** Lamports received. */
  readonly value: number;
  /** "$1.23" (at the payout price, else the label price) and "0.0082 SOL". */
  readonly usdText: string;
  readonly solText: string;
  /** NOTICE payoutDelayed arrived for this cash-out (show "held for review"). */
  readonly delayed: boolean;
  /** Practice rooms (money-mode §1.4): play money; the "Play for real" button. */
  readonly practice?: PracticeResults | null;
}

/**
 * The cash-out screen (APP_STATE.CASHED_OUT): ui/modals/cashout.ts, same chrome as the round
 * results.
 */
export interface CashOutScreen {
  readonly visible: boolean;
  show(msg: CashOutDoneMessage, view: CashOutView, nowMs: number, color?: Rgb): void;
  /** NOTICE payoutDelayed after the screen opened. */
  setDelayed(delayed: boolean): void;
  hide(): void;
  /** Esc while shown: Continue. */
  tryContinue(nowMs: number): boolean;
}

// ---------------------------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------------------------

/** HELLO clientBuild and the settings footer version. */
export const CLIENT_BUILD = '0.1.0';

/** The M1 debug readout flag: `?stats` in the page URL (roadmap M1). */
export function hasStatsFlag(search: string): boolean {
  return new URLSearchParams(search).has('stats');
}

/**
 * Debug: `?protocol=N` makes HELLO announce version N (roadmap M2 #11 forces 0 to see the
 * `outdated` dialog). Undefined when absent or not a u16.
 */
export function protocolOverride(search: string): number | undefined {
  const v = new URLSearchParams(search).get('protocol');
  if (v === null || v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 && n <= 0xffff ? n : undefined;
}

/** The menu layer (dimmed backdrop, 30 fps) is up in every state but playing and spectating. */
export function isMenuOpen(state: AppState): boolean {
  return state !== APP_STATE.PLAYING && state !== APP_STATE.SPECTATING;
}

/**
 * Whether the menu should re-ask /api/info on this state change: it just opened (Esc, death)
 * and the last ask is over `NET_TIMING.infoFreshSec` old (a fresh "N players online").
 */
export function refreshInfoOnOpen(s: AppState, prev: AppState, sinceAskMs: number): boolean {
  return isMenuOpen(s) && !isMenuOpen(prev) && sinceAskMs > NET_TIMING.infoFreshSec * 1000;
}

/** Whether `/api/info` lists `mode` as enabled. Without info only FFA (always on) is assumed. */
export function modeAvailable(info: ApiInfoResponse | null, mode: ModeId): boolean {
  if (!info) return mode === MODE.FFA;
  return info.modes[mode]?.enabled === true;
}

export interface BootTarget {
  /** The public mode to connect to (or to fall back to after a party). */
  readonly mode: ModeId;
  /** A party deep link to confirm first (ui-flow §10.4), or null. */
  readonly party: string | null;
}

/**
 * Boot target (ui-flow §3): a mode hash (`#ffa`, `#teams`, …) when that mode is enabled; a
 * party code hash when parties are enabled; otherwise the stored `ballio.mode` when it is an
 * enabled public mode; otherwise FFA.
 */
export function initialTarget(
  hash: string,
  storedMode: string,
  info: ApiInfoResponse | null,
): BootTarget {
  const stored = (PUBLIC_MODE_TILES as readonly string[]).includes(storedMode)
    ? (storedMode as ModeId)
    : null;
  const fallback = stored !== null && modeAvailable(info, stored) ? stored : MODE.FFA;
  const parsed = parseLocationHash(hash);
  if (parsed?.kind === 'mode' && modeAvailable(info, parsed.mode)) {
    return { mode: parsed.mode, party: null };
  }
  if (parsed?.kind === 'party' && modeAvailable(info, MODE.PARTY)) {
    return { mode: fallback, party: parsed.code };
  }
  return { mode: fallback, party: null };
}

/** The region this server belongs to (the one with an empty url), for "{n} playing in {region}". */
export function regionName(info: ApiInfoResponse | null): string {
  if (!info) return '';
  const here = info.regions.find((r) => r.url === '') ?? info.regions[0];
  return here?.name ?? '';
}

/** The toast text for a NOTICE (protocol §5.6), or null for notices handled another way. */
export function noticeText(msg: Pick<NoticeMessage, 'code' | 'arg'>): string | null {
  switch (msg.code) {
    case NOTICE.SPECTATE_FULL:
      return t('notice.spectateFull');
    case NOTICE.PARTY_JOINED:
      return t('notice.partyJoined', { name: displayName(msg.arg) });
    case NOTICE.PARTY_LEFT:
      return t('notice.partyLeft', { name: displayName(msg.arg) });
    case NOTICE.NAME_REJECTED:
      return t('notice.nameRejected');
    case NOTICE.SKIN_REJECTED:
      return t('notice.skinRejected');
    case NOTICE.CANNOT_SPECTATE:
      return t('notice.cannotSpectate');
    case NOTICE.BR_QUEUED:
      return t('notice.brQueued');
    default:
      // feedPaused / feedResumed (backdrop), roomClosing / serverRestart (banners), respawnIn
      // (Team Rush overlay, M3) and unknown codes produce no toast.
      return null;
  }
}

/** The ui-flow §9 dialog for a Connector give-up reason (partyGone is a toast instead). */
export function giveUpDialog(reason: GiveUpReason): DialogId | null {
  switch (reason) {
    case 'cantReach':
      return 'cant_reach';
    case 'roomFull':
      return 'room_full';
    case 'outdated':
      return 'outdated';
    case 'kicked':
      return 'kicked';
    case 'tooMany':
      return 'too_many';
    case 'partyGone':
    case 'moneyOff':
    case 'moneyRefused':
    case 'authInvalid':
    case 'practiceOff':
      return null;
  }
}

/** Auto graphics start level (ui-flow §4.5): Sharp on high-DPI screens up to 2560 px, else High. */
export function autoQualityLevel(devicePixelRatio: number, cssWidth: number): QualityLevel {
  return devicePixelRatio > 1 && cssWidth <= 2560 ? 'sharp' : 'high';
}

/** The graphics level for a setting ('auto' resolves by `autoQualityLevel`). */
export function resolveQualityLevel(
  setting: QualitySetting,
  devicePixelRatio: number,
  cssWidth: number,
): QualityLevel {
  return setting === 'auto' ? autoQualityLevel(devicePixelRatio, cssWidth) : setting;
}

// ---------------------------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------------------------

export interface AppOptions {
  readonly window: Window;
  /** `<canvas id="game">`. */
  readonly canvas: HTMLCanvasElement;
  /** `#hud`, `#menu`, `#modal-root`, `#toast-root` (ui-flow §1). */
  readonly hudRoot: HTMLElement;
  readonly menuRoot: HTMLElement;
  readonly modalRoot: HTMLElement;
  readonly toastRoot: HTMLElement;
  /**
   * Game server base for /api, /ws, /assets and /skins (net/server-url.ts `chooseServerBase`);
   * `""` (default) = this page's origin.
   */
  readonly serverBase?: string;
}

/** 30 fps menu cap (architecture §9.1), with 2 ms of slack for rAF timestamp jitter. */
const MENU_FRAME_MS = 1000 / 30 - 2;
/** ui-flow §2: connection lost while alive → home + dialog after 1 s. */
const LOST_DIALOG_DELAY_MS = 1000;
/** A 4002 close this soon after PLAY re-sends PLAY in the next room (ui-flow §9). */
const PLAY_RETRY_WINDOW_MS = 10_000;
const INFO_POLL_MS = NET_TIMING.infoPollSec * 1000;
const INFO_BOOT_TIMEOUT_MS = 2500;
const FONT_WAIT_MS = 3000;
/** Play hides the menu at once; it comes back if no spawn arrives this soon (ui-flow §4.2). */
const LAUNCH_TIMEOUT_MS = NET_TIMING.playTimeoutSec * 1000;
/** "Instant" for a renderer dim ramp. */
const DIM_INSTANT = 1e6;
/** The world fade-in under the results and the territory home's first show (SF-3, SF-5). */
const WORLD_FADE_MS = 2000;

interface PendingPlay {
  readonly nick: string;
  readonly skinId: string;
  readonly atMs: number;
  /** Money rooms: the entry ticket the PLAY carried (re-sent after a 4002 move). */
  readonly ticketId?: string;
  /**
   * A money PLAY with no ticket that takes back this account's own life, frozen after a dropped
   * socket (spec §9 reconnect): the spawn it answers consumed no ticket, so a kept one stays.
   */
  readonly resume?: true;
}

/** Calls `fn`, returning `fallback` if it throws (guards calls into optional subsystems). */
function attempt<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/** Runs `fn` on a later microtask so a synchronous throw becomes a rejection. */
function later<T>(fn: () => T | Promise<T>): Promise<T> {
  return Promise.resolve().then(fn);
}

export class App {
  /** The ui-flow §2 state; screens subscribe to it. */
  readonly state: ReadonlySignal<AppState>;
  protected readonly stateSig: Signal<AppState>;
  protected readonly opts: AppOptions;
  /** Server base for every server URL (net/server-url.ts); `""` = this page. */
  protected readonly serverBase: string;

  // --- DOM-free parts (built in the constructor) ---
  readonly settings: SettingsStore;
  readonly store = new WorldStore();
  readonly clock = new TickClock();
  readonly camera = new Camera();
  readonly socket: GameSocket;
  readonly connector: Connector;
  readonly input: InputSender;
  readonly sfx: Sfx;
  /** The client event bus (events/bus.ts): subscribe here for gameplay, money and UI events. */
  readonly bus: ClientBus = createClientBus();
  /** The money flow (stake select → buy-in → play); `MONEY_IDLE` in the free game. */
  readonly money: ReadonlySignal<MoneyFlowState>;
  /**
   * Sign-in (auth/session.ts): loads the auth module lazily (Privy only on "Sign in" or to restore
   * an earlier session), hands out the access token for money HELLOs and Bearer calls.
   */
  readonly account: AccountSession;
  protected readonly moneySig: Signal<MoneyFlowState>;
  /** The practice flow (money-mode §1.4); `PRACTICE_IDLE` outside the practice arena. */
  readonly practice: ReadonlySignal<PracticeFlowState>;
  protected readonly practiceSig: Signal<PracticeFlowState>;
  /** The first-play controls hint (U2 component; null until plugged in by `start()`'s owner). */
  onboarding: OnboardingHint | null = null;
  /** The cash-out screen (U1); `start()` defaults it to the summary fallback. */
  protected cashOutScreen!: CashOutScreen;
  protected readonly renderTap: RenderEventTap;

  // --- DOM parts (built by start()) ---
  protected home!: HomeScreen;
  protected summary!: SummaryScreen;
  protected leaderboard!: LeaderboardHud;
  protected score!: ScoreHud;
  protected stats!: StatsHud;
  protected banners!: BannersHud;
  protected renderer!: Renderer;
  protected textCache!: TextCache;
  protected assets!: AssetLoader;
  protected keyboard!: KeyboardInput;
  protected mouse!: MouseInput;
  protected touch!: TouchInput;
  /** Picks mouse / touch each display frame and drives the InputSender (movement.md §4.2–4.3). */
  protected steer!: SteerController;
  /** The first connected gamepad (ui-flow §11.4, M4), polled once per display frame. */
  protected readonly gamepad = new GamepadInput();
  private padEjectHeld = false;
  protected layout!: MenuLayout;
  protected modesCard!: ModesCard;
  protected moneyCard!: MoneyCard;
  protected practiceCard!: PracticeCard;
  protected moneyHud!: MoneyHud;
  protected spectateBar!: SpectateBar;
  protected dimLayer!: DimLayer;
  protected controlsHint: ControlsHint | null = null;
  /** The DOM arena dim (visuals §11) while the renderer has no `setDim`. */
  protected readonly dim = new DimRamp(DIM.MENU);

  // --- territory arena (lane C; docs/territory-arena.md §11) ---
  /** The arena client of the current room (WELCOME.arena), null in cell rooms. */
  arena: ArenaClient | null = null;
  /** The territory-first home (the front door) and the classic play card behind `home`. */
  protected territoryHome!: TerritoryHome;
  protected classicHome!: HomeScreen;
  /** Which menu front is shown: the territory home or the classic agar menu. */
  private front: 'territory' | 'classic' = 'territory';
  /** The home backdrop arena while not in an arena room (built on first use). */
  private backdrop: ArenaBackdrop | null = null;
  protected arenaResults!: ArenaResults;
  /** When the world fade (results, the home's first show) started; null = none running. */
  private worldFadeAtMs: number | null = null;
  /** The territory home's world fade-in ran (first show only). */
  private homeFaded = false;
  /** The canvas dim while the arena (or the backdrop) draws instead of the cell renderer. */
  private readonly arenaDim = new DimOverlay();
  /** The arena draws this frame (the HUD dim reads its overlay). */
  private arenaDrawn = false;
  /** Territory Play pressed outside an arena room: play once the practice arena is joined. */
  private pendingTerritoryPlay: string | null = null;
  /** The money panel on the territory home is open. */
  private moneyPanelOpen = false;
  /** A death / cash-out whose results open after the arena delay (UI §4.1, §4.3). */
  private arenaEndTimer: ReturnType<typeof setTimeout> | null = null;
  /** Dev `?arenaDemo`: the client-only arena drives the arena client (no server room). */
  private demo: DemoArena | null = null;
  private demoAccMs = 0;
  private demoAlive = false;

  // --- runtime state ---
  private started = false;
  private stopped = false;
  private readonly disposers: Unsubscribe[] = [];
  private rafId = 0;
  private lastFrameMs = Number.NaN;
  private stateChangedAtMs = 0;
  private info: ApiInfoResponse | null = null;
  private manifest: AssetManifest = EMPTY_MANIFEST;
  private welcome: WelcomeMessage | null = null;
  private mode: ModeId = MODE.FFA;
  /** The public mode to come back to (after a party, or a give-up). */
  private publicMode: ModeId = MODE.FFA;
  /** The first FRAME of the current socket has arrived. */
  private connected = false;
  /** A life is on screen (SPAWNED + OWN) and not yet ended (DEATH, connection loss). */
  private lifeActive = false;
  private lifeNick: string | null = null;
  private lifeColor: Rgb | null = null;
  private pendingPlay: PendingPlay | null = null;
  private feedPaused = false;
  private feedResumeSentAtMs = Number.NEGATIVE_INFINITY;
  private needSnap = true;
  private lbVersion = -1;
  private lbKey = '';
  /** A life or spectate session happened this page load (the leaderboard shows from then on). */
  private hadSession = false;
  /** The current life's first own cell was drawn (resume undims at once). */
  private firstCellSeen = false;
  /** performance.now() of the current life's spawn (money HUD: earliest cash-out). */
  private lifeStartMs = 0;
  /** The next home reveal is a death one ("Skip round summary"), not Esc. */
  private pendingReveal: 'esc' | 'death' | null = null;
  private launchTimer: ReturnType<typeof setTimeout> | null = null;
  /** NOTICE payoutDelayed arrived before CASH_OUT_DONE. */
  private payoutDelayedEarly = false;
  private roomVersion = -1;
  private assetsVersion = -1;
  private statsFlag = false;
  private viewW = 0;
  private viewH = 0;
  private dpr = 1;
  private qualityDirty = true;
  private quality: RenderQuality = qualityProfile('high');
  private lostTimer: ReturnType<typeof setTimeout> | null = null;
  private infoTimer: ReturnType<typeof setInterval> | null = null;
  /** performance.now() of the last /api/info ask (−∞ = none yet). */
  private infoAskedAtMs = Number.NEGATIVE_INFINITY;
  private readonly reported = new Set<string>();
  /** Money: the unused entry ticket (kept across a refused PLAY: a refusal leaves it usable). */
  private ticket: ApiMoneyTicket | null = null;
  /** Money: the cash-out key is held and CASH_OUT_START was sent. */
  private cashOutHeld = false;
  /** Money: /api/money/status poll while an earlier life of this account still runs. */
  private moneyWatchTimer: ReturnType<typeof setTimeout> | null = null;
  /** The next ask after a money `full` answer (pickStake), and how many came in a row. */
  private moneyFullTimer: ReturnType<typeof setTimeout> | null = null;
  private moneyFullAnswers = 0;
  /** Money: wall ms when the watch started (a newer `lastExit` is that life's outcome). */
  private moneyWatchSince = 0;
  /** Money: a NOTICE idleWarning is showing (cleared by the next input). */
  private idleWarned = false;
  /** The account changed during a money life: leave the money arena once that life ends. */
  private leaveMoneyAfterLife = false;
  /** The username dialog that is open (one at a time). */
  private usernameAsk: Promise<boolean> | null = null;
  /** DEATH waiting for its last own cell's eat to be drawn (`onDeath`). */
  private pendingDeath: {
    readonly msg: DeathMessage;
    readonly timer: ReturnType<typeof setTimeout>;
  } | null = null;

  constructor(opts: AppOptions) {
    this.opts = opts;
    this.stateSig = signal<AppState>(APP_STATE.BOOT);
    this.state = this.stateSig;
    this.settings = new SettingsStore();
    const search = attempt(() => opts.window.location.search, '');
    const protocolVersion = protocolOverride(search);
    this.serverBase = opts.serverBase ?? '';
    this.socket = new GameSocket({
      clientBuild: CLIENT_BUILD,
      baseUrl: this.serverBase,
      ...(protocolVersion !== undefined ? { protocolVersion } : {}),
    });
    this.account = new AccountSession({ baseUrl: this.serverBase });
    // Money targets only: the HELLO carries the access token (free rooms stay anonymous).
    this.connector = new Connector(this.socket, {
      baseUrl: this.serverBase,
      authToken: ({ fresh }) => this.account.token({ fresh }),
    });
    this.input = new InputSender(this.socket);
    this.sfx = new Sfx({ settings: () => this.settings.get() });
    this.moneySig = signal<MoneyFlowState>(MONEY_IDLE);
    this.money = this.moneySig;
    this.practiceSig = signal<PracticeFlowState>(PRACTICE_IDLE);
    this.practice = this.practiceSig;
    this.renderTap = new RenderEventTap(this.bus);
  }

  /**
   * Boot (ui-flow §3): settings, fonts, manifest + brand/UI art, /api/info, URL hash (or
   * `ballio.mode`, or ffa), connect; build the screens; start the main loop.
   */
  start(): void {
    if (this.started) return;
    this.started = true;
    const { window: win, canvas, hudRoot, menuRoot, modalRoot, toastRoot } = this.opts;
    const doc = win.document;
    this.statsFlag = hasStatsFlag(win.location.search);

    initModals(modalRoot);
    initToasts(toastRoot);
    this.applyTheme(this.settings.get());
    this.disposers.push(this.settings.subscribe((s, prev) => this.onSettings(s, prev)));
    this.watchSystemMotion();

    // Rendering. The loader starts on the empty manifest; the real one swaps in when fetched.
    this.assets = new AssetLoader(EMPTY_MANIFEST, { baseUrl: this.serverBase });
    this.textCache = new TextCache();
    // Static boot scene until the first Play/Spectate (visuals §11.1, owner: max parity / D-U3).
    this.renderer = new Renderer(canvas, {
      assets: this.assets,
      textCache: this.textCache,
      bootScene: true,
    });
    applyFavicon(doc, null);

    // Screens (ui-qol §4): the play card and the end-of-life cards share the centre column.
    const click = (source: UiClickSource): void => this.playClick(source);
    const touchUi = this.isTouchDevice();
    this.classicHome = createHome({
      onPlay: (nick) => this.onPlayPressed(nick),
      onSettings: () => this.openSettings(),
      playClick: click,
    });
    this.territoryHome = createTerritoryHome({
      onPlay: (nick) => this.onTerritoryPlay(nick),
      onMoney: () => this.onMoneyDoor(),
      onClassic: () => this.setFront('classic'),
      onSettings: () => this.openSettings(),
      onHelp: () => this.openHelp(),
      playClick: click,
      touch: touchUi,
    });
    // The arena skin button right of Play (SK-2): opens the picker; the pick is a setting.
    const skinButton = createSkinButton({
      settings: this.settings,
      playClick: click,
      dpr: () => Math.min(3, Math.max(1, win.devicePixelRatio || 1)),
    });
    this.territoryHome.setSkinButton(skinButton.el);
    this.disposers.push(() => skinButton.destroy());
    this.home = this.combinedHome();
    this.arenaResults = new ArenaResults(doc, {
      onContinue: () => this.onContinue(),
      onAgain: () => this.onArenaAgain(),
      onReveal: (i) =>
        this.sfx.trigger('reveal', performance.now(), i === 3 ? { rate: 4 / 3 } : {}),
      playClick: () => click('continue'),
    });
    this.summary = createSummary({
      onContinue: () => this.onContinue(),
      onPlayForReal: () => this.playForReal(),
      playClick: () => click('continue'),
    });
    const cashOut = createCashOutScreen({
      onContinue: () => this.onContinue(),
      onPlayForReal: () => this.playForReal(),
      playClick: () => click('cashOutScreen'),
      isTestMoney: () => (this.moneyCard.info ?? this.money.get().info)?.devBuyIn === true,
    });
    this.cashOutScreen = cashOut;
    this.modesCard = createModesCard({
      onSelect: (mode) => {
        this.selectMode(mode);
        this.home.focusNick();
      },
      playClick: click,
    });
    this.moneyCard = createMoneyCard({
      flow: this.money,
      fetchInfo: () => fetchMoneyInfo({ baseUrl: this.serverBase }),
      open: () => this.openMoney(),
      pickStake: (usd) => this.pickStake(usd),
      back: () => this.moneyBack(),
      leave: () => this.leaveMoney(),
      lifeActive: () => this.lifeActive && this.store.isMoneyRoom,
      roomPlayers: () => (this.store.isMoneyRoom ? (this.store.room.value?.playing ?? null) : null),
      cashOutKey: () => this.store.money.room?.cashOutKey ?? 'Q',
      formatValue: (v) => this.moneyLabel(v),
      touch: () => this.isTouchDevice(),
      playClick: click,
      account: {
        state: this.account.state,
        signIn: () => void this.signInFromMenu(),
        signOut: () => void this.signOutFromMenu(),
        pickUsername: () => void this.askUsername(true),
        copyWallet: (address) => this.copyWallet(address),
        requireAccount: () => this.requireAccount(),
      },
    });
    this.disposers.push(
      this.account.state.subscribe((s, prev) => this.onAccount(s.user, prev.user)),
      () => this.account.dispose(),
    );
    this.practiceCard = createPracticeCard({
      flow: this.practice,
      open: () => this.openPractice(),
      leave: () => this.leavePractice(),
      lifeActive: () => this.lifeActive && this.store.isPracticeRoom,
      roomPlayers: () =>
        this.store.isPracticeRoom ? (this.store.room.value?.playing ?? null) : null,
      rake: () => this.store.money.room?.rake ?? 0.1,
      playClick: click,
    });
    this.layout = createMenuLayout({
      window: win,
      center: [this.classicHome.el, this.summary.el, cashOut.el],
      // Territory-first (owner, 2026-10-31): practice and money live on the territory home; the
      // classic menu keeps its mode card.
      right: [this.modesCard.el],
      onHelp: () => this.openHelp(),
      playClick: click,
      version: CLIENT_BUILD,
      touch: touchUi,
    });
    menuRoot.classList.add('menu-layer');
    const backToTerritory = doc.createElement('button');
    backToTerritory.type = 'button';
    backToTerritory.className = 'front-back';
    backToTerritory.textContent = `← ${t('home.backToTerritory')}`;
    backToTerritory.addEventListener('click', () => {
      click('mode');
      this.setFront('territory');
    });
    menuRoot.replaceChildren(
      this.territoryHome.el,
      this.layout.el,
      this.layout.footer,
      backToTerritory,
      this.arenaResults.el,
    );
    this.applyFront();
    this.disposers.push(
      () => this.layout.dispose(),
      () => this.moneyCard.dispose(),
      () => this.practiceCard.dispose(),
      () => setDialogClick(null),
    );
    setDialogClick(() => click('dialog'));

    // HUD.
    this.leaderboard = createLeaderboard();
    this.score = createScore();
    this.stats = createStatsReadout();
    this.banners = createBanners();
    this.spectateBar = createSpectateBar(touchUi);
    this.moneyHud = createMoneyHud({ onHold: (held) => this.setCashOutHeld(held) });
    this.controlsHint = createControlsHint(win);
    this.onboarding = this.controlsHint;
    this.dimLayer = createDimLayer();
    hudRoot.classList.add('hud-layer');
    hudRoot.replaceChildren(
      this.banners.el,
      this.stats.el,
      this.leaderboard.el,
      this.score.el,
      ...this.moneyHud.els,
      this.spectateBar.el,
      this.controlsHint.el,
    );
    hudRoot.after(this.dimLayer.el);
    this.disposers.push(() => this.dimLayer.el.remove());
    this.stats.setVisible(this.statsFlag || this.settings.get().showStats);
    this.setDim(DIM.MENU, 0, performance.now());

    // Input.
    this.keyboard = new KeyboardInput(win);
    this.mouse = new MouseInput(canvas);
    this.keyboard.attach();
    this.mouse.attach();
    this.disposers.push(
      () => this.keyboard.detach(),
      () => this.mouse.detach(),
      this.keyboard.events.on('action', (a) => this.onKeyAction(a)),
      this.keyboard.events.on('ejectHeld', (held) => this.onEjectHeld(held)),
      // Money rooms: hold the cash-out key (WELCOME cashOutKey, default Q). Q stays SPECIAL while
      // spectating; setCashOutHeld only acts while a money life is playing.
      this.keyboard.events.on('cashOutHeld', (held) => this.setCashOutHeld(held)),
      this.mouse.events.on('wheel', ({ notches }) => this.onWheel(notches)),
    );
    // Touch (ui-flow §11.3): its controls live in the HUD layer and show only in play.
    this.touch = new TouchInput(canvas, hudRoot);
    this.touch.mode = this.settings.get().touchSteer;
    this.touch.attach();
    this.disposers.push(
      () => this.touch.detach(),
      this.touch.events.on('action', (a) => this.onKeyAction(a)),
      this.touch.events.on('ejectHeld', (held) => this.onEjectHeld(held)),
      this.touch.events.on('pinch', ({ factor }) => this.onPinch(factor)),
    );
    this.steer = new SteerController(this.input, this.mouse, this.touch, this.gamepad);
    this.disposers.push(
      this.gamepad.events.on('action', (a) => this.onKeyAction(a)),
      this.gamepad.events.on('zoom', ({ notches }) => this.onWheel(notches)),
      this.gamepad.events.on('connected', (on) => toast(t(on ? 'toast.padOn' : 'toast.padOff'))),
    );

    // Net.
    this.disposers.push(
      attachHandlers(this.socket, this.store, this.clock, this.sink, this.bus),
      this.socket.events.on('close', (c) => this.onSocketClose(c.byClient)),
      ...this.wireBus(),
      this.connector.events.on('connecting', () => this.onConnecting()),
      this.connector.events.on('connected', (ev) => this.onConnected(ev.target, ev.afterRoomFull)),
      this.connector.events.on('lost', () => this.onConnectionLost()),
      this.connector.events.on('gaveUp', ({ reason }) => this.onGaveUp(reason)),
      this.connector.events.on('full', ({ delayMs, retryAfterSec }) =>
        this.onServerFull(delayMs, retryAfterSec),
      ),
    );

    // Audio (audio.md): unlock on the first gesture (pointer, key, touchend, click: iOS needs
    // the last two) and play every gameplay / UI sound from the bus with the §3 gating.
    this.disposers.push(this.sfx.attachUnlock(win), attachAudioTriggers(this.bus, this.sfx));

    // Window.
    const userInput = (): void => this.onUserInput();
    this.listen(win, 'pointermove', userInput, true);
    this.listen(win, 'keydown', userInput, true);
    this.listen(win, 'wheel', userInput, true);
    this.listenKey(win, 'wheel', (ev) => this.onWindowWheel(ev as WheelEvent));
    this.listen(win, 'touchstart', userInput, true);
    this.listen(win, 'resize', () => {
      this.qualityDirty = true;
    });
    this.listen(win, 'pagehide', () => this.home.flushNick());
    this.listenKey(win, 'keydown', (ev) => this.onDebugKey(ev as KeyboardEvent));

    // State side effects.
    this.disposers.push(this.state.subscribe((s, prev) => this.onState(s, prev)));
    this.onState(this.state.get(), this.state.get());
    this.home.show();

    if (import.meta.env.DEV && /[?&]arenaDemo\b/.test(attempt(() => win.location.search, ''))) {
      this.startArenaDemo();
    }

    this.rafId = win.requestAnimationFrame(this.loop);
    void this.boot();
  }

  /** Stops the loop, closes the socket and removes listeners. */
  stop(): void {
    if (!this.started || this.stopped) return;
    this.stopped = true;
    this.opts.window.cancelAnimationFrame(this.rafId);
    if (this.lostTimer !== null) clearTimeout(this.lostTimer);
    if (this.infoTimer !== null) clearInterval(this.infoTimer);
    this.lostTimer = null;
    this.infoTimer = null;
    this.dropPendingDeath();
    this.cancelArenaEnd();
    this.arena?.destroy();
    this.arena = null;
    this.stopMoneyWatch();
    this.clearMoneyFullRetry();
    this.home.flushNick();
    attempt(() => this.connector.stop(), undefined);
    for (const off of this.disposers.splice(0)) attempt(off, undefined);
    closeAllModals();
    clearToasts();
    this.sfx.close();
    this.opts.hudRoot.replaceChildren();
    this.opts.menuRoot.replaceChildren();
  }

  /** Applies `transition` and publishes the new state (side effects subscribe to `state`). */
  dispatch(ev: AppEvent): void {
    const next = transition(this.stateSig.get(), ev);
    this.stateSig.set(next);
  }

  /** Settings ▸ Watch a game (M2): SPECTATE, then `spectating` (ui-flow §4.5, §8). */
  watch(): void {
    if (this.lifeActive || this.state.get() !== APP_STATE.HOME || !this.connected) return;
    this.input.spectate();
    this.camera.releaseHold();
    attempt(() => this.renderer.setBootScene(false), undefined);
    this.dispatch({ type: 'watch' });
  }

  /** A mode tile (M3): keep the state, store the choice, reconnect (ui-flow §4.3). */
  selectMode(mode: ModeId): void {
    if (!(PUBLIC_MODE_TILES as readonly string[]).includes(mode)) return;
    this.publicMode = mode;
    saveString(STORAGE_KEYS.mode, mode);
    this.setHash(`#${mode}`);
    this.practiceOff();
    this.connector.connect({ kind: 'mode', mode });
  }

  // -------------------------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------------------------

  private async boot(): Promise<void> {
    this.home.setConnecting(true);
    this.loadFonts().catch((err: unknown) => this.report(err));
    this.loadArt().catch((err: unknown) => this.report(err));
    this.infoAskedAtMs = performance.now();
    this.info = await later(() =>
      fetchInfo({ baseUrl: this.serverBase, timeoutMs: INFO_BOOT_TIMEOUT_MS }),
    ).catch(() => null);
    if (this.stopped) return;
    // Sign-in: only a returning signed-in player loads the auth module now (session restore).
    if (this.info !== null) this.account.setInfo(this.info.auth);
    this.practiceCard.setInfo(this.info?.practice ?? null);
    this.refreshPlayerCount();
    this.infoTimer = setInterval(() => this.pollInfo(), INFO_POLL_MS);
    const win = this.opts.window;
    const pick = initialTarget(win.location.hash, loadString(STORAGE_KEYS.mode, ''), this.info);
    this.publicMode = pick.mode;
    try {
      if (pick.party !== null) this.confirmPartyLink(pick.party);
      else if (this.territoryRoomFirst(win.location.hash)) this.openPractice();
      else this.connector.connect({ kind: 'mode', mode: pick.mode });
    } catch (err) {
      this.report(err);
    }
  }

  /**
   * SF-1: on the territory front (no mode or party in the URL) the home sits in the practice
   * arena from the start: its live room is the home's backdrop, and Play only sends PLAY over the
   * open socket (spawn ≈ 1 RTT after the click instead of a room join first).
   */
  private territoryRoomFirst(hash: string): boolean {
    if (this.demo || this.front !== 'territory') return false;
    if (hash !== '' && hash !== '#') return false;
    return this.info?.practice?.enabled === true;
  }

  /** Party deep link (ui-flow §10.4): Join, or Not now (clear the hash, load the default mode). */
  private confirmPartyLink(code: string): void {
    void confirmDialog(
      t('party.confirmJoin.title', { code }),
      t('party.confirmJoin.body'),
      t('party.join'),
      t('party.notNow'),
    ).then((join) => {
      if (this.stopped) return;
      if (join) {
        this.connector.connect({ kind: 'party', code });
      } else {
        this.setHash('');
        this.connector.connect({ kind: 'mode', mode: this.publicMode });
      }
    });
  }

  private async loadFonts(): Promise<void> {
    const fonts = (this.opts.window.document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts) return;
    const loaded = Promise.all([
      fonts.load('700 1em Fredoka'),
      fonts.load('600 1em Fredoka'),
      fonts.load('400 1em Ubuntu'),
    ]);
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, FONT_WAIT_MS));
    await Promise.race([loaded, timeout]).catch(() => undefined);
    if (this.stopped) return;
    // Names drawn before the font arrived were cached in the fallback face.
    attempt(() => this.textCache.clear(), undefined);
  }

  private async loadArt(): Promise<void> {
    const manifest = await later(() => fetchManifest({ baseUrl: this.serverBase })).catch(
      () => EMPTY_MANIFEST,
    );
    if (this.stopped) return;
    this.manifest = manifest;
    attempt(() => this.assets.setManifest(manifest), undefined);
    // Optional drop-in sounds (art-assets §8) replace their synthesised slots.
    void later(() => loadDropIns(this.sfx, manifest.files, { baseUrl: this.serverBase })).catch(
      (err: unknown) => this.report(err),
    );
    await later(() => this.assets.loadInitial(1500)).catch(() => undefined);
    if (this.stopped) return;
    this.refreshBrand();
  }

  /** /api/info every 30 s while the menu is open (protocol §3, §7). */
  private pollInfo(): void {
    if (isMenuOpen(this.state.get())) this.pollInfoNow();
  }

  // -------------------------------------------------------------------------------------------
  // Server messages (GameSink) and connection events
  // -------------------------------------------------------------------------------------------

  private readonly sink: GameSink = {
    welcome: (w) => this.onWelcome(w),
    firstFrame: () => this.onFirstFrame(),
    playing: (playerId) => this.onPlaying(playerId),
    death: (msg) => this.onDeath(msg),
    notice: (msg) => this.onNotice(msg),
    party: (msg) => this.onParty(msg),
    matchResult: (msg) => this.onMatchResult(msg),
    serverError: (msg) => this.onServerError(msg),
    cashOutDone: (msg) => this.onCashOutDone(msg),
    frame: (msg, atMs) => {
      // Dev `?arenaDemo` feeds the arena client itself; the real (classic) room stays out.
      if (!this.demo) this.arena?.applyFrame(msg, atMs);
    },
  };

  private onWelcome(w: WelcomeMessage): void {
    const now = performance.now();
    this.welcome = w;
    this.setupArena(w);
    // The boot /api/info ran before this socket existed, so "N players online" left us out.
    this.pollInfoNow();
    this.mode = modeIdFromCode(w.mode) ?? MODE.FFA;
    this.connected = false;
    // A new socket: a DEATH still waiting for its draw belongs to the old one.
    const pending = this.pendingDeath;
    this.dropPendingDeath();
    if (pending !== null && this.state.get() === APP_STATE.PLAYING) this.applyDeath(pending.msg);
    this.endLife();
    this.needSnap = true;
    this.camera.releaseHold();
    this.lbVersion = -1;
    this.lbKey = '';
    this.roomVersion = -1;
    this.leaderboard.update({ entries: [], yourRank: 0 });
    this.banners.clearBanner();
    this.setFeedPaused(false);
    this.input.reset();
    if (w.tickRate > 0) this.input.tickMs = 1000 / w.tickRate;
    this.pushOptions(now);
    this.input.resendOptions(now);
    this.renderTap.reset();
    this.cashOutHeld = false;
    attempt(() => this.keyboard.setCashOutKey(w.money?.cashOutKey ?? null), undefined);
    closeDialog('connecting');
  }

  private onFirstFrame(): void {
    this.connected = true;
    this.home.setConnecting(false);
    this.home.setOffline(false);
    this.dispatch({ type: 'firstFrame' });
    this.refreshPlayerCount();
    if (this.money.get().step === MONEY_FLOW.JOINING && this.store.isMoneyRoom) {
      this.moneyFlow({ type: 'joined' });
    }
    if (this.practice.get().step === PRACTICE_FLOW.JOINING && this.store.isPracticeRoom) {
      this.practiceSig.set({ step: PRACTICE_FLOW.READY });
    }
    this.practiceCard.refresh();
    // Territory Play pressed before the practice arena was joined: play now.
    const nick = this.pendingTerritoryPlay;
    if (nick !== null && this.store.isPracticeRoom) {
      this.pendingTerritoryPlay = null;
      if (!this.arena) this.territoryHome.setNote(t('home.noArena'));
      queueMicrotask(() => {
        if (!this.stopped && !this.lifeActive) this.onPlayPressed(nick);
      });
    }
  }

  private onPlaying(playerId: number): void {
    const queuedSpawn = this.pendingPlay === null;
    const resumed = this.pendingPlay?.resume;
    this.lifeActive = true;
    this.lifeNick = this.pendingPlay?.nick ?? finalNick(this.home.nick());
    this.lifeColor = this.store.players.get(playerId)?.color ?? null;
    this.pendingPlay = null;
    this.home.setWaiting(false);
    this.home.setAlive(true);
    this.hadSession = true;
    this.lifeStartMs = performance.now();
    this.payoutDelayedEarly = false;
    if (queuedSpawn && this.mode === MODE.BATTLE_ROYALE) toast(t('toast.brStart'));
    if (this.store.isMoneyRoom && !this.store.isPracticeRoom) {
      // The ticket was consumed by this spawn (money-mode §2.1 rule 4); a resumed life consumed
      // none, so a kept ticket stays for the next life.
      if (resumed !== true) {
        this.ticket = null;
        clearMoneyTicket();
      }
      this.moneyFlow({ type: 'spawned' });
    }
    this.practiceCard.refresh();
    if (this.arena) this.onArenaSpawned(playerId);
    this.dispatch({ type: 'playing' });
  }

  /**
   * DEATH arrives about one interpolation delay before the last own cell's eat is drawn (the
   * server is ahead of what is on screen). The death dim and the results screen start with that
   * drawn eat (`ownLastCellEaten`, render time, like the death sound), or DEATH_DRAW_WAIT_MS
   * after DEATH at the latest (review 2026-10-24: the screen used to darken while the cell was
   * still visibly alive).
   */
  private onDeath(msg: DeathMessage): void {
    if (this.arena) {
      this.onArenaLifeEnd('death', msg);
      return;
    }
    if (this.pendingDeath !== null) return;
    if (this.store.view.own.length === 0) {
      this.applyDeath(msg);
      return;
    }
    const timer = setTimeout(() => this.flushDeath(), DEATH_DRAW_WAIT_MS);
    this.pendingDeath = { msg, timer };
  }

  private flushDeath(): void {
    const p = this.pendingDeath;
    if (p === null) return;
    clearTimeout(p.timer);
    this.pendingDeath = null;
    this.applyDeath(p.msg);
  }

  private dropPendingDeath(): void {
    if (this.pendingDeath !== null) clearTimeout(this.pendingDeath.timer);
    this.pendingDeath = null;
  }

  private applyDeath(msg: DeathMessage): void {
    const before = this.state.get();
    this.endLife();
    this.moneyFlow({ type: 'lifeEnded' });
    // ui-qol §3: "Skip round summary" brings home back on the results schedule.
    this.pendingReveal = 'death';
    this.dispatch({ type: 'death', mode: this.mode, skipSummary: this.settings.get().skipSummary });
    this.pendingReveal = null;
    const after = this.state.get();
    if (after === APP_STATE.SUMMARY && this.arena) {
      this.showArenaResults('death', msg.arena ?? null, msg.valueLost ?? null, msg.timeAliveSec);
    } else if (after === APP_STATE.SUMMARY) {
      const lost = msg.valueLost;
      this.summary.show(msg, performance.now(), this.lifeColor ?? undefined, {
        valueLostText: lost !== undefined ? this.moneyLabel(lost) : null,
        practice: this.practiceResults(),
      });
    } else if (after === APP_STATE.HOME && before === APP_STATE.PLAYING) {
      // Skip round summary: home with the nick field focused (money rooms: the loss as a toast).
      this.home.focusNick();
      if (msg.valueLost !== undefined) {
        toast(t('money.valueLost', { v: this.moneyLabel(msg.valueLost) }), TOAST_MS, 'warning');
      }
    }
  }

  private onNotice(msg: NoticeMessage): void {
    const now = performance.now();
    switch (msg.code) {
      case NOTICE.FEED_PAUSED:
        this.setFeedPaused(true);
        return;
      case NOTICE.FEED_RESUMED:
        this.setFeedPaused(false);
        this.needSnap = true;
        return;
      case NOTICE.SERVER_RESTART:
        this.banners.showBanner('serverRestart', Number(msg.arg) || 0, now);
        return;
      case NOTICE.ROOM_CLOSING:
        this.banners.showBanner('roomClosing', Number(msg.arg) || 0, now);
        return;
      case NOTICE.IDLE_WARNING: {
        // money-mode §5: no input for a while freezes the money life (no un-freeze).
        const secs = Number(msg.arg) || 0;
        this.idleWarned = true;
        if (this.arena) {
          // Arena rooms: the forced exit (spec §9) — the arena's own banner.
          this.arena.idleWarning(secs, now);
          return;
        }
        this.banners.showBanner('idleWarning', secs, now);
        toast(t('money.idleWarning', { s: secs }), TOAST_MS, 'warning');
        return;
      }
      case NOTICE.IDLE_FROZEN: {
        const secs = Number(msg.arg) || 0;
        this.idleWarned = false;
        this.setCashOutHeld(false);
        this.banners.clearBanner('idleWarning');
        this.banners.showBanner('idleFrozen', secs, now);
        toast(t('money.idleFrozen', { s: secs }), TOAST_MS * 2, 'warning');
        return;
      }
      case NOTICE.BR_QUEUED:
        this.home.setWaiting(false);
        this.pendingPlay = null;
        break;
      case NOTICE.SPECTATE_FULL:
        if (this.state.get() === APP_STATE.SPECTATING) this.dispatch({ type: 'escape' });
        noticeBox(t('notice.spectateFull'));
        return;
      case NOTICE.NAME_REJECTED:
        // The server plays this life unnamed.
        if (this.pendingPlay) {
          this.pendingPlay = { ...this.pendingPlay, nick: '' };
        } else if (this.lifeActive) {
          this.lifeNick = '';
        }
        break;
    }
    const text = noticeText(msg);
    if (text !== null) toast(text);
  }

  private onParty(_msg: PartyMessage): void {
    // M3: party card / HUD code box.
  }

  private onMatchResult(_msg: MatchResultMessage): void {
    // M3: Battle Royale and Team Rush results modals.
  }

  private onServerError(msg: ErrorMessage): void {
    // The server closes right after an ERROR; the Connector maps the close code (ui-flow §9).
    // Money rooms: ERROR 11–16 are ticket / account problems, shown by the money flow.
    const kind = moneyErrorKindOf(msg.code);
    if (kind !== null) this.moneyFail({ kind: 'server', error: kind });
  }

  /**
   * CASH_OUT_DONE (money-mode §4): the life ended with a payout. The cash-out screen shows the
   * stats plus the amount in USD and SOL; the flow returns to `ready` (next life = new buy-in).
   */
  private onCashOutDone(msg: CashOutDoneMessage): void {
    if (this.arena) {
      this.onArenaLifeEnd('cashOut', msg);
      return;
    }
    this.endLife();
    this.cashOutHeld = false;
    this.moneyFlow({ type: 'lifeEnded' });
    this.dispatch({ type: 'cashOutDone' });
    if (this.state.get() !== APP_STATE.CASHED_OUT) return;
    const solUsd = msg.solUsd > 0 ? msg.solUsd : this.store.money.solUsd;
    const view: CashOutView = {
      value: msg.value,
      usdText: formatMoney(msg.value, 'USD', solUsd),
      solText: formatMoney(msg.value, 'SOL', solUsd),
      delayed: this.payoutDelayedEarly,
      practice: this.practiceResults(),
    };
    this.payoutDelayedEarly = false;
    this.cashOutScreen.show(msg, view, performance.now(), this.lifeColor ?? undefined);
  }

  /**
   * The server answered `full` (architecture §6.3).
   * - Free target: the Connector asks again in `delayMs` by itself. The play card says
   *   "Servers are full · Retrying in N s" until a room is found.
   * - Money target (the Connector's own find, the re-find after a dropped money socket, or after
   *   a 4002 to a money PLAY): free play was not refused, so the play card is left alone. The
   *   Connector has stopped; the money card shows its waiting state ("Every $N arena is full ·
   *   Trying again in N s", Cancel), the app asks /api/money/find again by itself, and the free
   *   arena comes back meanwhile. A bought ticket stays usable.
   */
  private onServerFull(delayMs: number, retryAfterSec?: number): void {
    const target = this.connector.target;
    if (target?.kind === 'money') {
      this.onMoneyServerFull(target.stakeUsd, retryAfterSec);
      return;
    }
    this.setLaunching(false);
    this.connected = false;
    this.home.setOffline(false);
    this.home.setServerFull(delayMs);
  }

  private onMoneyServerFull(stakeUsd: number, retryAfterSec: number | undefined): void {
    this.connected = false;
    this.home.setConnecting(false);
    if (this.money.get().step !== MONEY_FLOW.OFF) {
      this.moneyFail(this.scheduleMoneyFullRetry(stakeUsd, retryAfterSec));
    }
    if (this.connector.phase === CONNECTOR_PHASE.STOPPED) this.backToFreeGame();
  }

  private onConnecting(): void {
    this.setLaunching(false);
    this.connected = false;
    this.home.setOffline(false);
    this.home.setConnecting(true);
  }

  private onConnected(target: ConnectTarget, afterRoomFull: boolean): void {
    if (target.kind === 'mode') {
      this.publicMode = target.mode;
      saveString(STORAGE_KEYS.mode, target.mode);
    }
    const pending = this.pendingPlay;
    if (afterRoomFull && pending && performance.now() - pending.atMs < PLAY_RETRY_WINDOW_MS) {
      // ui-flow §9: the 4002 answered our PLAY, so play again in the new room. Deferred so every
      // WELCOME subscriber has run first.
      queueMicrotask(() => {
        if (this.stopped || this.lifeActive || this.pendingPlay !== pending) return;
        if (pending.resume === true) return; // the frozen life is in the old room, not this one
        if (pending.ticketId !== undefined) {
          if (target.kind !== 'money') return; // a money PLAY never goes to a free room
          this.socket.sendPlay(pending.nick, pending.skinId, pending.ticketId);
        } else {
          this.input.play(pending.nick, pending.skinId);
        }
        this.pendingPlay = { ...pending, atMs: performance.now() };
        this.home.setWaiting(true);
      });
    }
  }

  /** A working connection closed unexpectedly; the Connector is already retrying. */
  private onConnectionLost(): void {
    const s = this.state.get();
    const wasAlive = this.lifeActive;
    const money = this.connector.target?.kind === 'money';
    this.connected = false;
    this.cashOutHeld = false;
    this.idleWarned = false;
    this.endLife();
    this.banners.clearBanner();
    this.home.setConnecting(true);
    this.input.steering = false;
    if (money) {
      // territory-arena §9: the life stays in the arena, frozen (then the forced exit drives it
      // out and cashes it out) unless cut; the Connector rejoins the stake's arena meanwhile, the
      // status watch shows that life until it ends (then its outcome), and the next Play takes
      // it back (`buyInAndPlay` → a PLAY without a ticket).
      this.pendingPlay = null;
      this.home.setWaiting(false);
      this.moneyFlow({ type: 'rejoin' });
      if (wasAlive) this.watchMoneyStatus();
    }
    if (s !== APP_STATE.PLAYING && s !== APP_STATE.SPECTATING && !wasAlive) return;
    if (this.lostTimer !== null) clearTimeout(this.lostTimer);
    this.lostTimer = setTimeout(() => {
      this.lostTimer = null;
      this.dispatch({ type: 'connectionLost' });
      if (wasAlive) {
        this.openDialog(money ? 'money_connection_lost' : 'connection_lost', {
          onPrimary: () => this.home.focusNick(),
        });
      }
    }, LOST_DIALOG_DELAY_MS);
  }

  private onGaveUp(reason: GiveUpReason): void {
    const target = this.connector.target;
    if (target?.kind === 'money' && this.onMoneyGaveUp(target, reason)) return;
    if (target?.kind === 'practice' && reason === 'practiceOff') {
      // No practice arena on this server: say so on the card and bring the free game back.
      this.practiceSig.set({ step: PRACTICE_FLOW.ERROR });
      if (this.pendingTerritoryPlay !== null) {
        this.pendingTerritoryPlay = null;
        this.home.setWaiting(false);
        toast(t('practice.closed'), TOAST_MS, 'warning');
      }
      this.home.setConnecting(false);
      this.backToFreeGame();
      return;
    }
    if (this.lostTimer !== null) clearTimeout(this.lostTimer);
    this.lostTimer = null;
    this.connected = false;
    this.endLife();
    this.banners.clearBanner();
    this.home.setConnecting(false);
    const s = this.state.get();
    if (s === APP_STATE.PLAYING || s === APP_STATE.SPECTATING)
      this.dispatch({ type: 'connectionLost' });
    if (reason === 'partyGone') {
      toast(t('party.ended'));
      this.setHash('');
      this.connector.connect({ kind: 'mode', mode: this.publicMode });
      return;
    }
    this.home.setOffline(true);
    const id = giveUpDialog(reason);
    if (id === null) return;
    const win = this.opts.window;
    const onPrimary: (() => void) | undefined =
      id === 'cant_reach'
        ? () => this.retryBoot()
        : id === 'room_full' || id === 'too_many'
          ? () => this.retryConnect()
          : id === 'outdated'
            ? () => win.location.reload()
            : undefined;
    this.openDialog(id, onPrimary ? { onPrimary } : {});
  }

  /** cant_reach ▸ Retry: back to boot, a fresh first attempt, fresh /api/info. */
  private retryBoot(): void {
    this.dispatch({ type: 'retry' });
    this.retryConnect();
    this.pollInfoNow();
  }

  private retryConnect(): void {
    this.home.setOffline(false);
    this.home.setConnecting(true);
    this.connector.retry();
  }

  private pollInfoNow(): void {
    this.infoAskedAtMs = performance.now();
    later(() => fetchInfo({ baseUrl: this.serverBase }))
      .then((info) => {
        if (this.stopped) return;
        this.info = info;
        this.account.setInfo(info.auth);
        this.practiceCard.setInfo(info.practice);
        this.refreshPlayerCount();
      })
      .catch(() => undefined);
  }

  private openDialog(id: DialogId, opts: DialogOptions = {}): void {
    showDialog(id, opts);
  }

  /** The current life ended (death, connection loss or a new socket). */
  private endLife(): void {
    this.idleWarned = false;
    this.banners.clearBanner('idleWarning');
    this.banners.clearBanner('idleFrozen');
    this.lifeActive = false;
    this.lifeNick = null;
    this.firstCellSeen = false;
    this.home.setAlive(false);
    this.practiceCard?.refresh();
    this.applyFront();
  }

  private setFeedPaused(paused: boolean): void {
    this.feedPaused = paused;
    this.opts.menuRoot.classList.toggle('is-feed-paused', paused);
    if (!paused) this.feedResumeSentAtMs = Number.NEGATIVE_INFINITY;
  }

  /** Any pointer, key or touch input: a paused menu feed resumes with the current OPTIONS. */
  private onUserInput(): void {
    if (this.idleWarned && this.state.get() === APP_STATE.PLAYING) {
      // The input that answers an idle warning moves the target (or splits / ejects), which
      // resets the server's idle clock.
      this.idleWarned = false;
      this.banners.clearBanner('idleWarning');
    }
    if (!this.feedPaused) return;
    const now = performance.now();
    if (now - this.feedResumeSentAtMs < 1000) return;
    this.feedResumeSentAtMs = now;
    this.input.resendOptions(now);
  }

  // -------------------------------------------------------------------------------------------
  // Player actions
  // -------------------------------------------------------------------------------------------

  private onPlayPressed(nick: string): void {
    const s = this.state.get();
    this.playClick('play');
    if (this.lifeActive) {
      // ui-flow §5: Play reads Resume while alive; it hides the menu and does not respawn.
      if (s === APP_STATE.HOME) this.dispatch({ type: 'resume' });
      return;
    }
    if (s !== APP_STATE.HOME || !this.connected || this.pendingPlay) return;
    const clean = finalNick(nick);
    // Arena rooms take the arena skin pick (`t:<slug>`, SK-1); cell rooms the Classic skin.
    const skinId = this.arena !== null ? this.settings.get().arenaSkin : this.skinFor(clean);
    // Practice rooms (money-mode §1.4): PLAY without a ticket (the server issues a free one).
    if (this.store.isMoneyRoom && !this.store.isPracticeRoom) {
      this.buyInAndPlay(clean, skinId);
      return;
    }
    this.input.play(clean, skinId);
    this.pendingPlay = { nick: clean, skinId, atMs: performance.now() };
    this.home.setWaiting(true);
    this.setLaunching(true);
    // ui-flow §4.2: a spawn that never comes frees the button after 3 s (home does that); drop
    // the pending PLAY with it so the next press can send again.
    const pending = this.pendingPlay;
    setTimeout(() => {
      if (this.pendingPlay === pending) this.pendingPlay = null;
    }, NET_TIMING.playTimeoutSec * 1000);
  }

  /** The skin id for PLAY: the equipped skin, else a name skin (ui-flow §4.2, art-assets §7.6). */
  private skinFor(nick: string): string {
    const equipped = loadString(STORAGE_KEYS.skin, '');
    if (equipped.startsWith('local:')) return '';
    if (equipped !== '') return equipped;
    return attempt(() => nameSkinFor(this.manifest, nick), '');
  }

  private onContinue(): void {
    if (this.arenaResults.visible) {
      this.arenaResults.hide();
      this.arena?.spectate();
      this.worldFadeAtMs = null;
    }
    this.dispatch({ type: 'continue' });
    this.home.focusNick();
  }

  // -------------------------------------------------------------------------------------------
  // Click hook, bus wiring and onboarding
  // -------------------------------------------------------------------------------------------

  /**
   * The menu click hook (audio §3 row 7): every menu component gets `playClick` in its deps and
   * calls it on activation; this plays the `click` sound and emits `uiClick`. Settings
   * checkboxes and selects never call it (silent, audio §3 row 8).
   */
  playClick(source: UiClickSource = 'other'): void {
    this.bus.emit('uiClick', { source });
  }

  /** App-side bus subscriptions (onboarding hint, money HUD notices). */
  private wireBus(): Unsubscribe[] {
    const bus = this.bus;
    return [
      bus.on('firstOwnCellDrawn', () => {
        // ui-qol §3: the dim clears from the first drawn own cell (0.5 → 0 in 0.33 s).
        this.firstCellSeen = true;
        if (this.state.get() === APP_STATE.PLAYING) {
          this.setDim(0, DIM.UNDIM_RATE, performance.now());
        }
        const hint = this.onboarding;
        if (!hint) return;
        const money = this.store.isMoneyRoom;
        attempt(
          () =>
            hint.onFirstOwnCell({
              touch: this.isTouchDevice(),
              money,
              cashOutKey: this.store.money.room?.cashOutKey ?? 'Q',
            }),
          undefined,
        );
      }),
      bus.on('splitSent', () => this.onboarding?.onAction('split')),
      bus.on('ejectSent', () => this.onboarding?.onAction('eject')),
      bus.on('cashOutStartSent', () => this.onboarding?.onAction('cashOut')),
      bus.on('ownDeath', () => this.onboarding?.dismiss()),
      bus.on('ownLastCellEaten', () => this.flushDeath()),
      bus.on('cashOutDone', () => this.onboarding?.dismiss()),
      bus.on('cashOutTooEarly', ({ secondsLeft }) => {
        toast(t('money.tooEarly', { s: secondsLeft }));
      }),
      bus.on('moneyError', ({ kind }) => {
        // ERROR 11–16: the server refused the money life (protocol §5.6).
        void messageDialog(
          t('money.refused.title'),
          t(`money.error.${kind}` as I18nKey),
          t('common.ok'),
        );
      }),
      bus.on('moneyFlow', ({ step }) => {
        this.refreshPlayerCount();
        if (this.leaveMoneyAfterLife && step !== MONEY_FLOW.IN_LIFE) {
          // The account changed during that life: its arena is not this account's.
          this.leaveMoneyAfterLife = false;
          queueMicrotask(() => {
            if (!this.stopped) this.leaveMoney();
          });
        }
        const err = this.money.get().error;
        // ERROR 11–16 get their dialog above; every other failure is a warning toast, except a
        // `full` the app is already retrying (the card shows the countdown; not an error).
        const retrying = err?.kind === 'full' && err.retryAtMs !== undefined;
        if (step === MONEY_FLOW.ERROR && err && err.kind !== 'server' && !retrying) {
          toast(moneyErrorText(err), TOAST_MS, 'warning');
        }
      }),
      bus.on('payoutDelayed', () => {
        // Before CASH_OUT_DONE: the cash-out screen picks it up when it opens.
        if (this.state.get() === APP_STATE.CASHED_OUT) this.cashOutScreen.setDelayed(true);
        else this.payoutDelayedEarly = true;
      }),
      bus.on('ownCashOutStop', ({ completed }) => {
        // The server stopped the hold (split/eject, death): the key must be pressed again.
        if (!completed) this.cashOutHeld = false;
      }),
    ];
  }

  /** Whether touch controls are in use (coarse pointer). */
  private isTouchDevice(): boolean {
    return attempt(() => this.opts.window.matchMedia('(pointer: coarse)').matches, false);
  }

  /** Whether the controls hint would still show (for UI that wants to pre-load it). */
  onboardingPending(): boolean {
    return !isOnboardingSeen('controls') || (this.store.isMoneyRoom && !isOnboardingSeen('money'));
  }

  /** Marks the hint seen (the U2 component normally does this itself). */
  markOnboardingSeen(kind: 'controls' | 'money' = 'controls'): void {
    markOnboardingSeen(kind);
  }

  // -------------------------------------------------------------------------------------------
  // Money flow (money-mode §1.2, §2.1, §4; protocol §3)
  // -------------------------------------------------------------------------------------------

  /** Applies a money-flow event and publishes the state (and the `moneyFlow` bus event). */
  private moneyFlow(ev: MoneyFlowEvent, patch: Partial<MoneyFlowState> = {}): void {
    const cur = this.moneySig.get();
    const step = moneyTransition(cur.step, ev);
    if (step === cur.step && Object.keys(patch).length === 0) return;
    const next: MoneyFlowState = {
      ...cur,
      ...patch,
      step,
      error: step === MONEY_FLOW.ERROR ? (patch.error ?? cur.error) : null,
    };
    this.moneySig.set(next);
    if (step !== cur.step) this.bus.emit('moneyFlow', { step, prev: cur.step });
  }

  private moneyFail(error: MoneyFlowError): void {
    if (this.money.get().step === MONEY_FLOW.OFF) return;
    this.pendingPlay = null;
    this.home.setWaiting(false);
    this.setLaunching(false);
    // A refused PLAY leaves the ticket usable (money-mode §11), except a used or unknown one.
    if (
      error.kind === 'server' &&
      (error.error === 'ticketUsed' ||
        error.error === 'ticketInvalid' ||
        error.error === 'ticketNotYours')
    ) {
      this.ticket = null;
      clearMoneyTicket();
    } else if (error.kind === 'server' && this.ticket !== null) {
      toast(t('money.ticketKept', { stake: formatStake(this.ticket.stakeUsd) }), TOAST_MS);
    }
    this.moneyFlow({ type: 'failed' }, { error });
    // Another life of this account blocks Play: watch it, so Play works again once it ends.
    if (error.kind === 'server' && error.error === 'moneyLifeActive') this.watchMoneyStatus();
  }

  /**
   * Menu "Play for money": loads /api/info.money for the stake select. Resolves false when money
   * rooms are off on this server (the menu should then hide the entry point).
   */
  async openMoney(): Promise<boolean> {
    const info = await later(() => fetchMoneyInfo({ baseUrl: this.serverBase })).catch(() => null);
    if (this.stopped) return false;
    if (!info || !info.enabled || info.stakes.length === 0) {
      this.moneySig.set({ ...this.moneySig.get(), info });
      return false;
    }
    this.moneyFlow({ type: 'open' }, { info });
    if (this.money.get().step !== MONEY_FLOW.STAKE_SELECT) {
      this.moneySig.set({ ...this.moneySig.get(), info });
    }
    return true;
  }

  /**
   * A stake was picked: find its room (/api/money/find), then join it. The menu stays open over
   * the money room's arena; Play then buys in. The head count is in `money.roomHumans`.
   *
   * The find here only reports the head count and the full / disabled / network errors before
   * the switch; the Connector's money target then finds again and connects.
   */
  async pickStake(stakeUsd: number): Promise<void> {
    const step = this.money.get().step;
    if (step !== MONEY_FLOW.STAKE_SELECT && step !== MONEY_FLOW.READY) return;
    if (this.lifeActive) return;
    saveMoneyStake(stakeUsd);
    this.practiceOff();
    this.moneyFlow({ type: 'pickStake' }, { stakeUsd, roomHumans: null });
    this.home.setConnecting(true);
    const found = await later(() => findMoneyRoom(stakeUsd, { baseUrl: this.serverBase })).catch(
      () => null,
    );
    if (this.stopped || this.money.get().step !== MONEY_FLOW.JOINING) return;
    if (found !== null && found.status === 'full') {
      // Not an error: wait (growing) and ask again by ourselves; the free arena stays meanwhile.
      this.home.setConnecting(false);
      this.moneyFail(this.scheduleMoneyFullRetry(stakeUsd, found.retryAfterSec));
      if (this.connector.phase === CONNECTOR_PHASE.STOPPED) this.backToFreeGame();
      return;
    }
    if (found === null || found.status !== 'ok') {
      this.home.setConnecting(false);
      this.moneyFullAnswers = 0;
      this.moneyFail(found === null ? { kind: 'network' } : { kind: 'disabled' });
      if (this.connector.phase === CONNECTOR_PHASE.STOPPED) this.backToFreeGame();
      return;
    }
    this.moneyFullAnswers = 0;
    if (this.money.get().frozenLife === null) this.watchMoneyStatus();
    this.moneySig.set({ ...this.moneySig.get(), roomHumans: found.humans });
    // The Connector owns the money socket: it re-finds and reconnects after a drop (money-mode
    // §5) and gives up on the money refusals (closes 4011–4015). A life frozen by the drop is taken
    // back by the next Play (territory-arena §9: `buyInAndPlay` sends PLAY without a ticket).
    this.connector.connect({ kind: 'money', stakeUsd });
  }

  /** Back to the stake list (from ready or error); stays in the money room until a new pick. */
  moneyBack(): void {
    this.clearMoneyFullRetry();
    this.moneyFullAnswers = 0;
    this.moneyFlow({ type: 'back' });
  }

  /** Leave money mode: back to the free game (reconnects to the public mode). */
  leaveMoney(): void {
    if (this.money.get().step === MONEY_FLOW.OFF) return;
    if (this.lifeActive && this.store.isMoneyRoom) return; // cash out (or die) first
    this.ticket = null;
    this.leaveMoneyAfterLife = false;
    this.setCashOutHeld(false);
    this.stopMoneyWatch();
    this.clearMoneyFullRetry();
    this.moneyFullAnswers = 0;
    const prev = this.money.get().step;
    this.moneySig.set(MONEY_IDLE);
    this.bus.emit('moneyFlow', { step: MONEY_FLOW.OFF, prev });
    if (this.territoryRoomFirst('')) this.openPractice();
    else this.backToFreeGame();
  }

  // -------------------------------------------------------------------------------------------
  // Practice flow (money-mode §1.4)
  // -------------------------------------------------------------------------------------------

  /**
   * Menu "Practice arena": joins the practice room (no sign-in, no buy-in). Leaves the money flow
   * first when it is open without a life; refused during any life (cash out or die first).
   */
  openPractice(): void {
    if (this.stopped || this.lifeActive) return;
    if (this.practice.get().step === PRACTICE_FLOW.JOINING) return;
    if (this.money.get().step !== MONEY_FLOW.OFF) {
      this.ticket = null;
      this.setCashOutHeld(false);
      this.stopMoneyWatch();
      this.clearMoneyFullRetry();
      this.moneyFullAnswers = 0;
      const prev = this.money.get().step;
      this.moneySig.set(MONEY_IDLE);
      this.bus.emit('moneyFlow', { step: MONEY_FLOW.OFF, prev });
    }
    this.practiceSig.set({ step: PRACTICE_FLOW.JOINING });
    this.home.setConnecting(true);
    this.connector.connect({ kind: 'practice' });
  }

  /** Back to the free game from the practice arena (not during a practice life). */
  leavePractice(): void {
    if (this.practice.get().step === PRACTICE_FLOW.OFF) return;
    if (this.lifeActive && this.store.isPracticeRoom) return;
    this.practiceOff();
    this.backToFreeGame();
  }

  /** Forget the practice flow (another door was taken: a mode tile, a stake, Leave). */
  private practiceOff(): void {
    if (this.practice.get().step === PRACTICE_FLOW.OFF) return;
    this.practiceSig.set(PRACTICE_IDLE);
  }

  /**
   * The results screens' practice buttons (money-mode §1.4), or null outside practice:
   * "Play for real · $1" while the server sells money arenas, else a disabled "coming soon".
   */
  private practiceResults(): PracticeResults | null {
    if (!this.store.isPracticeRoom) return null;
    const info = this.moneyCard.info ?? this.money.get().info;
    const stake = info?.stakes[0] ?? this.store.money.room?.stakeUsd ?? 1;
    const enabled = info !== null && info.enabled && info.stakes.length > 0;
    return {
      realText: enabled
        ? t('practice.playForReal', { stake: formatStake(stake) })
        : t('practice.playForRealSoon'),
      realEnabled: enabled,
    };
  }

  /**
   * Results ▸ "Play for real · $1": leave practice and open the money door (the stake list, or
   * the money card's "coming soon" while money is off on this server).
   */
  playForReal(): void {
    if (this.lifeActive) return;
    this.dispatch({ type: 'continue' });
    this.leavePractice();
    void this.openMoney();
  }

  /**
   * After a money `full` answer: arms the next ask (growing backoff, at least the server's
   * retryAfterSec) and returns the flow error that carries its time for the card's countdown.
   * The ask itself is quiet (no "Joining…", no blocker): still full → wait again; anything else
   * → the normal pickStake path.
   */
  private scheduleMoneyFullRetry(
    stakeUsd: number,
    retryAfterSec: number | undefined,
  ): MoneyFlowError {
    this.clearMoneyFullRetry();
    this.moneyFullAnswers++;
    const delayMs = fullRetryDelayMs(this.moneyFullAnswers, retryAfterSec, Math.random);
    this.moneyFullTimer = setTimeout(() => {
      this.moneyFullTimer = null;
      const waiting = (): boolean => {
        const flow = this.money.get();
        return (
          !this.stopped &&
          flow.step === MONEY_FLOW.ERROR &&
          flow.error?.kind === 'full' &&
          flow.stakeUsd === stakeUsd
        );
      };
      if (!waiting()) return;
      void later(() => findMoneyRoom(stakeUsd, { baseUrl: this.serverBase }))
        .catch(() => null)
        .then((found) => {
          if (!waiting()) return;
          if (found !== null && found.status === 'full') {
            const error = this.scheduleMoneyFullRetry(stakeUsd, found.retryAfterSec);
            this.moneySig.set({ ...this.money.get(), error });
            return;
          }
          this.moneyFlow({ type: 'back' });
          void this.pickStake(stakeUsd);
        });
    }, delayMs);
    return { kind: 'full', retryAtMs: performance.now() + delayMs };
  }

  private clearMoneyFullRetry(): void {
    if (this.moneyFullTimer !== null) clearTimeout(this.moneyFullTimer);
    this.moneyFullTimer = null;
  }

  private backToFreeGame(): void {
    this.connector.connect({ kind: 'mode', mode: this.publicMode });
  }

  /**
   * The Connector gave up on the money target. Returns true when handled here:
   * - 'moneyRefused' (ERROR 11–16, already shown by the flow): rejoin the same arena, staying in
   *   the money flow with the reason on the card (Play tries again; a kept ticket is reused);
   * - 'roomFull' / 'moneyOff' / 'cantReach': the flow fails and the free game comes back.
   * - 'authInvalid' (close 4017 twice): signed out, "Please sign in again", the free game back.
   * Other reasons (outdated, kicked, tooMany) fall through to the ui-flow §9 dialogs.
   */
  private onMoneyGaveUp(
    target: { readonly kind: 'money'; readonly stakeUsd: number },
    reason: GiveUpReason,
  ): boolean {
    if (reason === 'moneyRefused') {
      this.connected = false;
      this.endLife();
      this.connector.connect(target);
      return true;
    }
    if (reason === 'authInvalid') {
      // The HELLO token was refused twice (a fresh one included): sign out, say so, free game.
      if (this.lostTimer !== null) clearTimeout(this.lostTimer);
      this.lostTimer = null;
      this.connected = false;
      this.endLife();
      this.banners.clearBanner();
      this.home.setConnecting(false);
      const s = this.state.get();
      if (s === APP_STATE.PLAYING || s === APP_STATE.SPECTATING) {
        this.dispatch({ type: 'connectionLost' });
      }
      this.leaveMoney();
      void this.account.signOut();
      this.showSignInAgain();
      return true;
    }
    if (reason === 'roomFull' || reason === 'moneyOff' || reason === 'cantReach') {
      if (this.lostTimer !== null) clearTimeout(this.lostTimer);
      this.lostTimer = null;
      this.connected = false;
      this.endLife();
      this.banners.clearBanner();
      this.home.setConnecting(false);
      const s = this.state.get();
      if (s === APP_STATE.PLAYING || s === APP_STATE.SPECTATING) {
        this.dispatch({ type: 'connectionLost' });
      }
      this.moneyFail(
        reason === 'roomFull'
          ? { kind: 'full' }
          : reason === 'moneyOff'
            ? { kind: 'disabled' }
            : { kind: 'disconnected' },
      );
      this.backToFreeGame();
      return true;
    }
    // Bans at PLAY arrive as KICKED (money-mode §11): re-read the account so the row shows it.
    if (reason === 'kicked') void this.account.refreshAccount();
    this.moneyFail({ kind: 'disconnected' });
    return false;
  }

  /**
   * Polls /api/money/status every MONEY_STATUS_POLL_MS while this account has a money life
   * running elsewhere (a dropped socket's frozen life, or another tab): the card shows its value
   * and auto-cash-out countdown; when it ends, a toast says how (cashed out / eaten). Stops by
   * itself once no life is reported, and with `leaveMoney`.
   */
  private watchMoneyStatus(): void {
    if (this.moneyWatchTimer !== null) return;
    this.moneyWatchSince = Date.now() - 5000;
    const poll = (): void => {
      this.moneyWatchTimer = null;
      if (this.stopped || this.money.get().step === MONEY_FLOW.OFF) return;
      void later(async () => {
        const token = await this.account.token();
        if (token === null) return undefined;
        return fetchMoneyStatus(token, { baseUrl: this.serverBase });
      })
        .catch(() => undefined)
        .then((st) => {
          if (this.stopped || this.money.get().step === MONEY_FLOW.OFF) return;
          if (st === undefined) {
            this.moneyWatchTimer = setTimeout(poll, MONEY_STATUS_POLL_MS);
            return;
          }
          if (this.applyMoneyStatus(st)) {
            this.moneyWatchTimer = setTimeout(poll, MONEY_STATUS_POLL_MS);
          }
        });
    };
    this.moneyWatchTimer = setTimeout(poll, 0);
  }

  private stopMoneyWatch(): void {
    if (this.moneyWatchTimer !== null) clearTimeout(this.moneyWatchTimer);
    this.moneyWatchTimer = null;
  }

  /**
   * Shows an earlier life from /api/money/status (null status = money off). Returns true while
   * one is still running (keep watching). A life of this page's own current spawn is not "earlier".
   */
  private applyMoneyStatus(st: ApiMoneyStatus | null): boolean {
    const cur = this.money.get();
    const life = st?.life ?? null;
    const own = this.lifeActive && this.store.isMoneyRoom;
    if (life !== null && !own && !life.reserved) {
      const frozenLife = {
        value: life.value,
        cashOutAtMs: performance.now() + (life.frozen ? life.autoCashOutInSec * 1000 : 0),
      };
      this.moneySig.set({ ...cur, frozenLife: life.frozen ? frozenLife : null });
      return true;
    }
    if (life !== null) return true; // reserved (a PLAY in flight) or this page's life
    const busy =
      cur.step === MONEY_FLOW.ERROR &&
      cur.error?.kind === 'server' &&
      cur.error.error === 'moneyLifeActive';
    if (busy) {
      // The life that blocked the account ended: Play works again in this arena.
      this.moneySig.set({ ...cur, step: MONEY_FLOW.READY, error: null, frozenLife: null });
      this.bus.emit('moneyFlow', { step: MONEY_FLOW.READY, prev: cur.step });
    } else if (cur.frozenLife !== null) {
      this.moneySig.set({ ...cur, frozenLife: null });
    }
    const exit = st?.lastExit ?? null;
    if (exit !== null && exit.atMs >= this.moneyWatchSince && !own) {
      this.moneyWatchSince = exit.atMs + 1;
      toast(
        exit.reason === 'death'
          ? t('money.exit.eaten')
          : t('money.exit.cashedOut', { v: this.moneyLabel(exit.paid) }),
        TOAST_MS,
        exit.reason === 'death' ? 'warning' : undefined,
      );
    }
    return false;
  }

  /**
   * Play in a money room: buy in (dev: POST /api/money/ticket with the signed-in account's Bearer
   * token), then PLAY with the ticket. An unused ticket from a refused PLAY is reused. The real
   * wallet buy-in replaces `requestDevTicket` only. Money needs an account: signed out (or banned,
   * or without a username) asks first and does not buy.
   */
  private buyInAndPlay(nick: string, skinId: string): void {
    const step = this.money.get().step;
    if (step !== MONEY_FLOW.READY && step !== MONEY_FLOW.ERROR) return;
    const stakeUsd = this.money.get().stakeUsd ?? this.store.money.room?.stakeUsd ?? 0;
    const user = this.account.user;
    if (user === null || user.username === null || isBanned(this.account.state.get(), Date.now())) {
      this.moneyFail(user === null ? { kind: 'signIn' } : { kind: 'username' });
      void this.requireAccount();
      return;
    }
    const account = user.accountId;
    const send = (ticket: ApiMoneyTicket): void => {
      if (this.stopped || this.lifeActive || !this.connected) {
        this.home.setWaiting(false);
        return;
      }
      this.socket.sendPlay(nick, skinId, ticket.id);
      this.pendingPlay = { nick, skinId, atMs: performance.now(), ticketId: ticket.id };
      this.moneyFlow({ type: 'ticketSent' });
    };
    // This account's own life, frozen in this arena after a dropped socket: take it back with a
    // PLAY that carries no ticket (spec §9; the server resumes it and buys nothing).
    const resume = (): void => {
      if (this.stopped || this.lifeActive || !this.connected) {
        this.home.setWaiting(false);
        return;
      }
      this.socket.sendPlay(nick, skinId);
      this.pendingPlay = { nick, skinId, atMs: performance.now(), resume: true };
      this.moneyFlow({ type: 'ticketSent' });
    };
    const frozenHere = (st: ApiMoneyStatus | null | undefined): boolean => {
      const life = st?.life ?? null;
      return (
        life !== null &&
        life.frozen &&
        life.frozenReason === 'disconnect' &&
        life.roomId !== '' &&
        life.roomId === this.store.info?.roomId
      );
    };
    // A paid entry not played yet (this tab, or kept from an earlier visit) is used first, unless
    // an earlier life may still be frozen here (then the status check below decides).
    const kept =
      this.ticket !== null && this.ticket.stakeUsd === stakeUsd && this.ticket.account === account
        ? this.ticket
        : loadMoneyTicket(account, stakeUsd);
    if (kept !== null && this.money.get().frozenLife === null) {
      this.ticket = kept;
      this.home.setWaiting(true);
      send(kept);
      return;
    }
    if (step === MONEY_FLOW.ERROR) {
      // Try again from the error card: back to ready in the same arena first.
      this.moneySig.set({ ...this.money.get(), step: MONEY_FLOW.READY, error: null });
    }
    this.home.setWaiting(true);
    this.moneyFlow({ type: 'buy' });
    const opts = { baseUrl: this.serverBase };
    const buying = (): boolean =>
      !this.stopped &&
      this.money.get().step === MONEY_FLOW.BUYING &&
      this.account.user?.accountId === account;
    const tokenRefused = (auth: string | null | undefined): boolean =>
      auth === 'authInvalid' || auth === 'authRequired';
    void later(async () => {
      // Ask first: a refusal the server would give for sure (one live money life per account,
      // ERROR 14) must never cost a stake.
      const status = await this.account.authed(
        (token) =>
          fetchMoneyStatus(token, opts).then(
            (st) => ({ st, err: null }),
            (err: unknown) => ({ st: undefined, err }),
          ),
        (r) => r.err instanceof MoneyHttpError && tokenRefused(r.err.auth),
      );
      if (!buying()) return;
      if (status.status !== 'done') {
        this.moneyFail({ kind: 'auth' });
        this.showSignInAgain();
        return;
      }
      const st = status.value.st;
      const err = status.value.err;
      if (err instanceof MoneyHttpError && err.auth === 'banned') {
        this.moneyFail({ kind: 'buyIn', error: 'banned' });
        void this.account.refreshAccount();
        this.showBanned();
        return;
      }
      if (frozenHere(st)) {
        resume();
        return;
      }
      if (kept !== null && !st?.life) {
        this.ticket = kept;
        send(kept);
        return;
      }
      if (st?.life) {
        this.moneyFail({ kind: 'server', error: 'moneyLifeActive' });
        this.applyMoneyStatus(st);
        this.watchMoneyStatus();
        void messageDialog(
          t('money.refused.title'),
          t('money.error.moneyLifeActive'),
          t('common.ok'),
        );
        return;
      }
      const bought = await this.account.authed(
        (token) =>
          requestDevTicket(token, stakeUsd, opts).catch(
            () => ({ status: 'error', error: 'network', httpStatus: 0 }) as const,
          ),
        (r) => r.status === 'error' && tokenRefused(r.error),
      );
      if (bought.status !== 'done') {
        if (buying()) {
          this.moneyFail({ kind: 'auth' });
          this.showSignInAgain();
        }
        return;
      }
      const res = bought.value;
      if (!buying()) {
        // Paid while the flow moved on: keep the entry for the next Play of this account.
        if (res.status === 'ok') {
          this.ticket = res.ticket;
          saveMoneyTicket(res.ticket);
        }
        return;
      }
      if (res.status !== 'ok') {
        this.moneyFail({ kind: 'buyIn', error: res.error });
        if (res.error === 'banned') {
          void this.account.refreshAccount();
          this.showBanned();
        }
        return;
      }
      this.ticket = res.ticket;
      saveMoneyTicket(res.ticket);
      send(res.ticket);
    }).catch((err: unknown) => {
      this.report(err);
      if (buying()) this.moneyFail({ kind: 'network' });
    });
  }

  // -------------------------------------------------------------------------------------------
  // Sign-in (auth/session.ts; protocol §3.2)
  // -------------------------------------------------------------------------------------------

  /** Menu "Sign in": the provider's modal; a first sign-in without a username asks for one. */
  private async signInFromMenu(): Promise<void> {
    const ok = await this.account.signIn();
    if (this.stopped) return;
    if (!ok) {
      if (this.account.state.get().loadFailed) toast(t('account.unavailable'), TOAST_MS, 'warning');
      return;
    }
    if (this.account.needsUsername) await this.askUsername(true);
  }

  /** Menu "Sign out" (not during a money life: cash out or die first). */
  private async signOutFromMenu(): Promise<void> {
    if (this.lifeActive && this.store.isMoneyRoom) return;
    await this.account.signOut();
    if (!this.stopped) toast(t('account.signedOut'));
  }

  /**
   * The username dialog (skippable after the first sign-in, required before a money game).
   * Resolves true once the account has a username.
   */
  askUsername(skippable: boolean): Promise<boolean> {
    if (!this.account.needsUsername) return Promise.resolve(this.account.signedIn);
    if (this.usernameAsk !== null) return this.usernameAsk;
    const ask = openUsernameDialog({
      skippable,
      submit: (name) => this.account.setUsername(name),
      playClick: () => this.playClick('account'),
    }).then((name) => {
      this.usernameAsk = null;
      if (name === undefined) return false;
      if (name !== null && !this.stopped) toast(t('username.saved', { name }));
      return !this.account.needsUsername;
    });
    this.usernameAsk = ask;
    return ask;
  }

  /**
   * Before money play (a stake button, or Play in a money arena): signed in, not banned, with a
   * username. Asks for whatever is missing; resolves false when the player backs out.
   */
  async requireAccount(): Promise<boolean> {
    if (this.account.state.get().phase === 'unavailable') {
      toast(t('account.unavailable'), TOAST_MS, 'warning');
      return false;
    }
    if (!this.account.signedIn) {
      const ok = await this.account.signIn();
      if (!ok || this.stopped) {
        if (!this.stopped && this.account.state.get().loadFailed) {
          toast(t('account.unavailable'), TOAST_MS, 'warning');
        }
        return false;
      }
    }
    if (isBanned(this.account.state.get(), Date.now())) {
      this.showBanned();
      return false;
    }
    if (this.account.needsUsername) return this.askUsername(false);
    return true;
  }

  /** The account changed (sign-in, sign-out, a session that ended by itself). */
  private onAccount(user: AuthUser | null, prev: AuthUser | null): void {
    this.moneyCard.refresh();
    if ((user?.accountId ?? null) === (prev?.accountId ?? null)) return;
    // A kept entry and the money socket belong to the account that bought / opened them.
    if (this.ticket !== null && this.ticket.account !== user?.accountId) this.ticket = null;
    if (prev === null || this.money.get().step === MONEY_FLOW.OFF) return;
    if (this.lifeActive && this.store.isMoneyRoom) {
      this.leaveMoneyAfterLife = true;
      return;
    }
    this.leaveMoney();
  }

  private copyWallet(address: string): void {
    const done = (): void => toast(t('account.walletCopied'));
    const clip = this.opts.window.navigator?.clipboard;
    if (clip && typeof clip.writeText === 'function') {
      clip.writeText(address).then(done, () => toast(t('common.copyFallback')));
    } else {
      toast(t('common.copyFallback'));
    }
  }

  private showSignInAgain(): void {
    void messageDialog(t('account.expired.title'), t('account.expired.body'), t('common.ok'));
  }

  private showBanned(): void {
    const line = banLine(this.account.user?.bannedUntil, Date.now());
    void messageDialog(
      t('account.banned.title'),
      line !== null ? `${t('account.banned.body')} ${line}` : t('account.banned.body'),
      t('common.ok'),
    );
  }

  /**
   * The cash-out key (Q, `money.cashOutKey`) or the touch hold button went down / up: sends
   * CASH_OUT_START / CASH_OUT_CANCEL once per change, only while a money life is playing. The
   * server runs the timer; the ring animates from `store.ownCashOutProgress(now)`.
   */
  setCashOutHeld(held: boolean): void {
    const can =
      held && this.store.isMoneyRoom && this.lifeActive && this.state.get() === APP_STATE.PLAYING;
    if (can && !this.cashOutHeld) {
      this.cashOutHeld = true;
      this.socket.cashOutStart();
    } else if (!held && this.cashOutHeld) {
      this.cashOutHeld = false;
      this.socket.cashOutCancel();
    }
  }

  /** Any socket close: a cash-out hold cannot outlive it (the Connector handles the rest). */
  private onSocketClose(_byClient: boolean): void {
    this.cashOutHeld = false;
  }

  private onKeyAction(action: KeyAction): void {
    const s = this.state.get();
    switch (action) {
      case 'escape':
        this.onEscape();
        return;
      case 'enter':
        this.onEnter();
        return;
      case 'split':
        if (this.arena) return; // arena rooms: no Split, no Shoot (spec §11)
        if (s === APP_STATE.PLAYING && this.lifeActive) {
          this.input.split();
        }
        return;
      case 'eject':
        if (this.arena) return;
        if (s === APP_STATE.PLAYING && this.lifeActive) {
          this.input.eject();
        }
        return;
      case 'special':
        // ui-flow §11.1: Q switches the spectate camera; it does nothing in play.
        if (s === APP_STATE.SPECTATING) this.input.special();
        return;
    }
  }

  private onEscape(): void {
    if (hasOpenModal()) {
      closeTopModal();
      return;
    }
    const s = this.state.get();
    if (this.arenaResults.visible) {
      this.arenaResults.tryContinue(performance.now());
    } else if (s === APP_STATE.SUMMARY) {
      this.summary.tryContinue(performance.now());
    } else if (s === APP_STATE.CASHED_OUT) {
      this.cashOutScreen.tryContinue(performance.now());
    } else if (s === APP_STATE.PLAYING || s === APP_STATE.SPECTATING) {
      this.dispatch({ type: 'escape' });
    }
    // ui-qol §11: Esc again on the Esc menu does nothing (Play resumes).
  }

  /**
   * ui-qol §11: Enter plays only from the nick field (its form submits; the keyboard does not
   * emit Enter while a text field is focused), and does nothing on the results screens.
   */
  private onEnter(): void {
    // Classic: intentionally empty (parity). The arena results take Enter (UI §4.2).
    if (this.arenaResults.visible) this.arenaResults.tryContinue(performance.now());
  }

  /**
   * Money HUD (money-mode §4): seconds until a cash-out may start, `minLifeSec` after the spawn
   * (the server's NOTICE cashOutTooEarly has the real figure).
   */
  private secondsUntilCashOut(minLifeSec: number): number {
    return minLifeSec - (performance.now() - this.lifeStartMs) / 1000;
  }

  private onEjectHeld(held: boolean): void {
    if (this.arena) return;
    const now = performance.now();
    const playing = this.state.get() === APP_STATE.PLAYING && this.lifeActive;
    this.input.setEjectHeld(held && playing, this.settings.get().holdToEject, now);
  }

  private onWheel(notches: number): void {
    if (this.arena) return; // the arena camera has no wheel or pinch (spec §11)
    const s = this.state.get();
    if (s !== APP_STATE.PLAYING && s !== APP_STATE.SPECTATING && !this.canZoomBackdrop()) return;
    this.camera.zoomBy(notches);
    this.pushOptions(performance.now());
  }

  /**
   * The results screens and home after a life or a spectate show the live arena (visuals §10):
   * the wheel zooms it there too (not on the boot scene, never over a dialog).
   */
  private canZoomBackdrop(): boolean {
    const s = this.state.get();
    if (s !== APP_STATE.SUMMARY && s !== APP_STATE.HOME && s !== APP_STATE.CASHED_OUT) return false;
    return this.hadSession && !hasOpenModal();
  }

  /**
   * Window wheel (the menu layer covers the canvas): zooms the backdrop per `canZoomBackdrop`,
   * except over form fields and anything that scrolls.
   */
  private onWindowWheel(ev: WheelEvent): void {
    if (ev.target === this.opts.canvas || !this.canZoomBackdrop()) return;
    if (ev.ctrlKey || ev.metaKey) return;
    let el = ev.target instanceof Element ? ev.target : null;
    while (el !== null && el !== this.opts.window.document.body) {
      if (el.matches('input, textarea, select, [role="dialog"], [role="listbox"]')) return;
      if (el.scrollHeight > el.clientHeight + 1) {
        const oy = this.opts.window.getComputedStyle(el).overflowY;
        if (oy === 'auto' || oy === 'scroll') return;
      }
      el = el.parentElement;
    }
    const n = wheelNotches(ev.deltaY, ev.deltaMode);
    if (n !== 0) this.onWheel(n);
  }

  /** Pinch: continuous zoom in the wheel's range (ui-flow §11.3). */
  private onPinch(factor: number): void {
    if (!(factor > 0) || !Number.isFinite(factor)) return;
    this.onWheel(Math.log(factor) / Math.log(WHEEL_ZOOM.step));
  }

  // -------------------------------------------------------------------------------------------
  // State, settings and theme
  // -------------------------------------------------------------------------------------------

  private onState(s: AppState, prev: AppState): void {
    const now = performance.now();
    this.stateChangedAtMs = now;
    if (s !== prev) this.bus.emit('appState', { state: s, prev });
    if (s !== APP_STATE.PLAYING) this.setCashOutHeld(false);
    if (s !== APP_STATE.PLAYING && s !== prev) this.onboarding?.dismiss();
    if (s !== prev) this.setLaunching(false);
    this.uiTransition(s, prev, now);
    const menuOpen = isMenuOpen(s);
    // The menu just came back (Esc, death): a fresh players-online count unless one is recent.
    if (refreshInfoOnOpen(s, prev, now - this.infoAskedAtMs)) this.pollInfoNow();
    const { menuRoot, canvas } = this.opts;
    menuRoot.classList.toggle('is-open', menuOpen);
    menuRoot.hidden = !menuOpen;

    const homeVisible = s === APP_STATE.BOOT || s === APP_STATE.HOME;
    if (homeVisible && (!this.home.visible || prev !== s)) this.home.show();
    else if (!homeVisible && this.home.visible) this.home.hide();
    if (s !== APP_STATE.CASHED_OUT && this.cashOutScreen.visible) this.cashOutScreen.hide();
    if (s !== APP_STATE.SUMMARY && s !== APP_STATE.CASHED_OUT && this.summary.visible) {
      this.summary.hide();
    }
    this.layout.setScreen(
      s === APP_STATE.SUMMARY || s === APP_STATE.CASHED_OUT ? 'results' : 'home',
    );
    this.moneyCard.setActive(homeVisible);

    // ui-qol §2.1: no board before the first Play/Spectate of the page load; then it stays, under
    // the dim, in every state.
    if (s === APP_STATE.PLAYING || s === APP_STATE.SPECTATING) this.hadSession = true;
    this.leaderboard.setVisible(this.hadSession && s !== APP_STATE.BOOT);
    this.spectateBar.setVisible(s === APP_STATE.SPECTATING);
    this.touch.setActive(!this.arena && (s === APP_STATE.PLAYING || s === APP_STATE.SPECTATING));
    this.touch.setSpectating(s === APP_STATE.SPECTATING);
    canvas.classList.toggle('is-playing', s === APP_STATE.PLAYING);
    if (s !== APP_STATE.SUMMARY && s !== APP_STATE.CASHED_OUT && this.arenaResults.visible) {
      this.arenaResults.hide();
    }
    this.applyFront();

    // Arena rooms steer with ARENA_INPUT (arena/controls.ts), never INPUT.
    this.input.steering = !this.arena && (s === APP_STATE.PLAYING || s === APP_STATE.SPECTATING);
    if (s !== APP_STATE.PLAYING) {
      this.input.setEjectHeld(false, this.settings.get().holdToEject, now);
    } else {
      // Keys must reach the game, not a menu field that kept focus.
      const active = this.opts.window.document.activeElement;
      if (active instanceof HTMLElement && active !== this.opts.window.document.body) active.blur();
    }
    this.pushOptions(now);
  }

  /** OPTIONS from the current state (InputSender sends it only when it changed). */
  private pushOptions(now: number): void {
    const s = this.settings.get();
    this.input.setOptions(
      {
        wantMinimapPlayers: s.minimapPlayers && (this.welcome?.minimapPlayersAllowed ?? false),
        menuOpen: isMenuOpen(this.state.get()),
        wheelZoom: this.camera.wheelZoom,
      },
      now,
    );
  }

  private onSettings(s: Settings, prev: Settings): void {
    this.applyTheme(s);
    if (s.quality !== prev.quality) this.qualityDirty = true;
    this.stats.setVisible(this.statsFlag || s.showStats);
    if (s.minimapPlayers !== prev.minimapPlayers) this.pushOptions(performance.now());
    if (s.volume !== prev.volume) this.sfx.applyVolume();
    this.touch.mode = s.touchSteer;
  }

  private applyTheme(s: Settings): void {
    const root = this.opts.window.document.documentElement;
    root.dataset['theme'] = s.darkMode ? 'dark' : 'light';
    root.dataset['style'] = s.style;
    root.dataset['motion'] = resolveReduceMotion(s) ? 'reduce' : 'full';
  }

  /** Re-applies the theme when the system reduced-motion preference changes (setting = follow). */
  private watchSystemMotion(): void {
    const mq = attempt(() => this.opts.window.matchMedia('(prefers-reduced-motion: reduce)'), null);
    if (!mq) return;
    const onChange = (): void => this.applyTheme(this.settings.get());
    mq.addEventListener('change', onChange);
    this.disposers.push(() => mq.removeEventListener('change', onChange));
  }

  private setHash(hash: string): void {
    const win = this.opts.window;
    attempt(() => {
      const url = new URL(win.location.href);
      url.hash = hash;
      win.history.replaceState(win.history.state, '', url.toString());
    }, undefined);
  }

  private listen(target: EventTarget, type: string, fn: () => void, capture = false): void {
    const opts: AddEventListenerOptions = { capture, passive: true };
    target.addEventListener(type, fn, opts);
    this.disposers.push(() => target.removeEventListener(type, fn, opts));
  }

  private listenKey(target: EventTarget, type: string, fn: (ev: Event) => void): void {
    const opts: AddEventListenerOptions = { passive: true };
    target.addEventListener(type, fn, opts);
    this.disposers.push(() => target.removeEventListener(type, fn, opts));
  }

  // -------------------------------------------------------------------------------------------
  // Main loop
  // -------------------------------------------------------------------------------------------

  private readonly loop = (now: number): void => {
    if (this.stopped) return;
    this.rafId = this.opts.window.requestAnimationFrame(this.loop);
    this.frame(now);
  };

  /**
   * One display frame. Each stage is guarded on its own, so a failing subsystem (say the
   * renderer) never stops input or the HUD; each distinct error is logged once.
   */
  private frame(now: number): void {
    const s = this.state.get();
    if (isMenuOpen(s) && now - this.lastFrameMs < MENU_FRAME_MS) return;
    const dt = Number.isFinite(this.lastFrameMs)
      ? Math.min(Math.max(now - this.lastFrameMs, 0), 1000)
      : 1000 / 60;
    this.lastFrameMs = now;

    if (this.arenaFrame(now, dt, s)) return;
    this.arenaDrawn = false;
    try {
      this.syncSize();
      const rt = this.clock.renderTick(now);
      const view = this.store.sample(rt);
      this.renderTap.afterSample(this.store);
      this.snapCamera();
      this.camera.update(dt, view.own, this.store.observerCamera, this.viewW, this.viewH);
    } catch (err) {
      this.report(err);
    }
    try {
      attempt(() => this.gamepad.poll(), undefined);
      if (this.gamepad.ejectHeld !== this.padEjectHeld) {
        this.padEjectHeld = this.gamepad.ejectHeld;
        this.onEjectHeld(this.padEjectHeld);
      }
      this.steer.frame(now, this.camera, {
        own: this.store.ownNewest(),
        rttMs: this.socket.rttMs,
        lastFrameAtMs: this.clock.lastFrameAtMs,
        tickMs: this.clock.dtEstMs,
        lastTick: this.clock.lastTick,
        tickSec: this.store.tickMs / 1000,
        stopOnRelease: this.settings.get().touchStopOnRelease,
      });
    } catch (err) {
      this.report(err);
    }
    try {
      this.renderer.draw(this.camera, this.store, this.settings.get(), this.quality, now);
    } catch (err) {
      this.report(err);
    }
    try {
      this.updateHud(now, s);
    } catch (err) {
      this.report(err);
    }
  }

  /** Canvas size and graphics level (backing store = CSS size × quality scale). */
  private syncSize(): void {
    const win = this.opts.window;
    const w = Math.max(1, win.innerWidth);
    const hgt = Math.max(1, win.innerHeight);
    const dpr = win.devicePixelRatio || 1;
    if (!this.qualityDirty && w === this.viewW && hgt === this.viewH && dpr === this.dpr) return;
    this.qualityDirty = false;
    this.viewW = w;
    this.viewH = hgt;
    this.dpr = dpr;
    const level = resolveQualityLevel(this.settings.get().quality, dpr, w);
    this.quality = qualityProfile(level, dpr);
    this.renderer.resize(w, hgt, this.quality.scale);
  }

  /** First frame of a room (or a feed resume): jump to the server's camera instead of panning. */
  private snapCamera(): void {
    if (!this.needSnap) return;
    const cam = this.store.observerCamera;
    if (cam) {
      this.needSnap = false;
      const zoom = cameraZoom(cam.viewScale, this.viewW, this.viewH, this.camera.wheelZoom);
      this.camera.snap(cam.x, cam.y, zoom);
    } else if (this.store.view.own.length > 0) {
      this.needSnap = false;
    }
  }

  private updateHud(now: number, s: AppState): void {
    const playing = s === APP_STATE.PLAYING;
    this.score.set(playing && this.lifeActive ? this.store.own.score : null);

    const settings = this.settings.get();
    const currency = resolveLabelCurrency(settings.labelCurrency, this.store.money.room);
    const solUsd = this.store.money.solUsd;
    const lb = this.store.leaderboard;
    const lbKey = `${lb.version}|${currency}|${solUsd.toFixed(2)}|${this.lifeNick ?? ''}`;
    if (lbKey !== this.lbKey) {
      this.lbKey = lbKey;
      this.lbVersion = lb.version;
      if (lb.value) this.leaderboard.update(lb.value, this.leaderboardOptions(lb.value, currency));
    }
    const room = this.store.money.room;
    this.moneyHud.update(
      playing && this.lifeActive && room !== null
        ? {
            value: this.store.ownValue,
            currency,
            solUsd,
            progress: this.store.ownCashOutProgress(performance.now()),
            key: room.cashOutKey,
            secondsUntilCashOut: this.secondsUntilCashOut(room.minLifeSec),
            touch: this.isTouchDevice(),
            practice: room.practice === true,
          }
        : null,
    );
    this.renderDim(now);
    const roomFeed = this.store.room;
    if (roomFeed.version !== this.roomVersion) {
      this.roomVersion = roomFeed.version;
      this.refreshPlayerCount();
    }
    if (this.assets.version !== this.assetsVersion) {
      this.assetsVersion = this.assets.version;
      this.refreshBrand();
    }
    this.stats.frame(now, this.socket.rttMs);
    const inGame = playing || s === APP_STATE.SPECTATING;
    // ui-qol §2.4: the laggy chip only shows with the FPS & ping readout.
    const showStats = this.statsFlag || settings.showStats;
    this.banners.setLaggy(showStats && this.connected && inGame && this.clock.isStalled(now));
    this.banners.tick(now);
  }

  /**
   * The menu cards' live data: mode tiles from /api/info, the source link, the money card's head
   * count, the buy-in line and the play card's "N players online" (/api/info.online).
   */
  private refreshPlayerCount(): void {
    const available = new Set<ModeId>();
    for (const mode of PUBLIC_MODE_TILES) if (modeAvailable(this.info, mode)) available.add(mode);
    const flow = this.money.get();
    this.modesCard.set(available, this.publicMode, flow.step !== MONEY_FLOW.OFF);
    const sourceUrl = this.info?.sourceUrl ?? '';
    this.layout.setSourceUrl(sourceUrl);
    this.home.setSourceUrl(sourceUrl);
    this.moneyCard.refresh();
    const room = this.store.money.room;
    const buyIn =
      room !== null && flow.step !== MONEY_FLOW.OFF && flow.step !== MONEY_FLOW.IN_LIFE
        ? buyInText(room.stakeUsd, room.rake)
        : null;
    const test = (this.moneyCard.info ?? flow.info)?.devBuyIn === true;
    this.home.setMoneyNote(buyIn, test);
    this.home.setOnline(this.info?.online ?? null);
    const minfo = this.moneyCard.info ?? flow.info;
    this.territoryHome.setMoneyDoor(
      minfo !== null && minfo.enabled && minfo.stakes.length > 0 ? 'live' : 'soon',
    );
  }

  // -------------------------------------------------------------------------------------------
  // UI helpers (ui-qol §3, §5, §9)
  // -------------------------------------------------------------------------------------------

  /** Settings (gear on the play card). Closing it focuses the nick field (ui-qol §9.4). */
  openSettings(): void {
    if (hasOpenModal()) return;
    openSettingsModal({
      store: this.settings,
      info: () => this.info,
      currentRegion: () => loadString(STORAGE_KEYS.region, 'auto'),
      onRegion: (id) => this.switchRegion(id),
      canSpectate: () => !this.lifeActive && this.connected && this.state.get() === APP_STATE.HOME,
      onSpectate: () => this.watch(),
      onShowHintAgain: () => this.controlsHint?.reset(),
      minimapPlayersAllowed: () => this.welcome?.minimapPlayersAllowed ?? false,
      playClick: (source) => this.playClick(source),
      touch: this.isTouchDevice(),
      version: CLIENT_BUILD,
      onClose: () => this.home.focusNick(),
    });
  }

  /** The "?" help modal (ui-qol §9.3). */
  openHelp(): void {
    if (hasOpenModal()) return;
    openHelp({
      onShowHintAgain: () => this.controlsHint?.reset(),
      cashOutKey: () =>
        this.store.money.room?.cashOutKey ??
        ((this.moneyCard.info ?? this.money.get().info)?.enabled ? 'Q' : null),
      playClick: (source) => this.playClick(source),
      onClose: () => this.home.focusNick(),
    });
  }

  /** Region select: saves `ballio.region`; another region's server loads via `?server=`. */
  private switchRegion(id: string): void {
    const region = this.info?.regions.find((r) => r.id === id);
    if (!region) return;
    saveString(STORAGE_KEYS.region, id);
    if (region.url === '') return;
    attempt(() => {
      const url = new URL(this.opts.window.location.href);
      url.searchParams.set('server', region.url);
      this.opts.window.location.assign(url.toString());
    }, undefined);
  }

  /** A lamport amount in the label unit (money-mode §7). */
  private moneyLabel(lamports: number): string {
    const currency = resolveLabelCurrency(this.settings.get().labelCurrency, this.store.money.room);
    return formatMoney(lamports, currency, this.store.money.solUsd);
  }

  private leaderboardOptions(
    lb: { readonly amountKind?: number },
    currency: ReturnType<typeof resolveLabelCurrency>,
  ): LeaderboardOptions {
    const kind = lb.amountKind;
    const solUsd = this.store.money.solUsd;
    return {
      ownNick: this.lifeNick ?? finalNick(this.home.nick()),
      formatAmount:
        kind === undefined
          ? null
          : (amount) =>
              formatLeaderboardAmount(
                amount,
                kind as Parameters<typeof formatLeaderboardAmount>[1],
                currency,
                solUsd,
              ),
    };
  }

  /**
   * The arena dim (visuals §11): the renderer's canvas dim when it has `setDim`, else the DOM
   * layer. `ratePerSec` 0 = instant.
   */
  private setDim(target: number, ratePerSec: number, now: number): void {
    this.arenaDim.setDim(target, ratePerSec > 0 ? ratePerSec : DIM_INSTANT);
    const r = this.renderer as unknown as { setDim?: (target: number, ratePerSec: number) => void };
    if (typeof r.setDim === 'function') {
      const fn = r.setDim.bind(this.renderer);
      attempt(() => fn(target, ratePerSec > 0 ? ratePerSec : DIM_INSTANT), undefined);
      this.dim.set(0, 0, now);
      return;
    }
    this.dim.set(target, ratePerSec, now);
  }

  /**
   * Draws the dim: the DOM layer while the renderer has no canvas dim; with the canvas dim, the
   * HUD layer gets the matching brightness so it reads as dimmed too (agar's HUD is on its canvas).
   */
  private renderDim(now: number): void {
    const canvasDim = this.arenaDrawn
      ? this.arenaDim.dim
      : (this.renderer as unknown as { dim?: unknown }).dim;
    const hud = this.opts.hudRoot;
    if (typeof canvasDim === 'number') {
      this.dimLayer.render(0);
      const filter = hudDimFilter(canvasDim);
      if (hud.style.filter !== filter) hud.style.filter = filter;
      return;
    }
    this.dimLayer.render(this.dim.valueAt(now));
  }

  /** ui-qol §3: Esc reveals the menu after 1.03 s, a skipped summary at +2.95 s. */
  private setReveal(kind: 'esc' | 'death' | null): void {
    const menu = this.opts.menuRoot;
    delete menu.dataset['reveal'];
    if (kind === null) return;
    void menu.offsetWidth; // restart the animation
    menu.dataset['reveal'] = kind;
  }

  /** Play pressed in a free room: the menu hides at once until the spawn (or a timeout). */
  private setLaunching(on: boolean): void {
    if (this.launchTimer !== null) clearTimeout(this.launchTimer);
    this.launchTimer = null;
    this.opts.menuRoot.classList.toggle('is-launching', on);
    if (on) {
      this.launchTimer = setTimeout(() => {
        this.launchTimer = null;
        this.opts.menuRoot.classList.remove('is-launching');
        this.home.setWaiting(false);
      }, LAUNCH_TIMEOUT_MS);
    }
  }

  /** Dim and menu-reveal side of a state change (ui-qol §3, visuals §11). */
  private uiTransition(s: AppState, prev: AppState, now: number): void {
    if (s === prev) return;
    const S = APP_STATE;
    const fromGame = prev === S.PLAYING || prev === S.SPECTATING;
    if (this.arena && (s === S.SUMMARY || s === S.CASHED_OUT || s === S.PLAYING)) {
      // Arena rooms (SF-2, SF-3): the spawn is an instant cut, and the only darkening under the
      // results is the results' own 0.8 black (the world fades in under it).
      this.setReveal(null);
      this.setDim(0, 0, now);
    } else if (s === S.HOME && fromGame) {
      const kind = this.pendingReveal ?? 'esc';
      this.setReveal(kind);
      this.setDim(DIM.MENU, kind === 'death' ? DIM.DEATH_RATE : DIM.ESC_RATE, now);
    } else if (s === S.SUMMARY) {
      this.setReveal(null);
      this.setDim(DIM.MENU, DIM.DEATH_RATE, now);
    } else if (s === S.CASHED_OUT) {
      this.setReveal(null);
      this.setDim(DIM.MENU, DIM.ESC_RATE, now);
    } else if (s === S.SPECTATING) {
      this.setReveal(null);
      this.setDim(0, DIM.UNDIM_RATE, now);
    } else if (s === S.PLAYING) {
      this.setReveal(null);
      // Resume: the cells are on screen already. A new life undims on its first drawn cell.
      if (this.firstCellSeen) this.setDim(0, DIM.UNDIM_RATE, now);
    } else {
      this.setReveal(null);
      if (s === S.BOOT) this.setDim(DIM.MENU, 0, now);
    }
  }

  /** Backslash toggles the FPS & ping readout (ui-qol §11). */
  private onDebugKey(ev: KeyboardEvent): void {
    if (ev.code !== 'Backslash' || ev.repeat || ev.ctrlKey || ev.metaKey || ev.altKey) return;
    const target = ev.target as { closest?: (sel: string) => unknown } | null;
    if (target?.closest?.('input, textarea, select')) return;
    this.settings.set('showStats', !this.settings.get().showStats);
  }

  /** The owner's wordmark and favicon replace the placeholders once loaded (art-assets §3). */
  private refreshBrand(): void {
    const doc = this.opts.window.document;
    this.home.setWordmark(attempt(() => this.assets.image('brand/wordmark'), null));
    const favicon = attempt(() => this.assets.image('brand/favicon'), null);
    if (favicon) applyFavicon(doc, favicon);
  }

  // -------------------------------------------------------------------------------------------
  // Territory arena (lane C; docs/territory-arena.md §11, territory-ui.md)
  // -------------------------------------------------------------------------------------------

  /** `home` for the rest of the app: the front that is shown (territory or classic). */
  private combinedHome(): HomeScreen {
    const c = this.classicHome;
    const tt = this.territoryHome;
    const territory = (): boolean => this.front === 'territory';
    return {
      get el(): HTMLElement {
        return c.el;
      },
      get visible(): boolean {
        return territory() ? tt.visible : c.visible;
      },
      show: (): void => {
        if (territory()) {
          c.hide();
          tt.show(performance.now());
        } else {
          tt.hide();
          c.show();
        }
      },
      hide: (): void => {
        c.hide();
        tt.hide();
      },
      focusNick: (): void => (territory() ? tt.focusNick() : c.focusNick()),
      nick: (): string => (territory() ? tt.nick() : c.nick()),
      setAlive: (v: boolean): void => {
        c.setAlive(v);
        tt.setAlive(v);
      },
      setWaiting: (v: boolean): void => {
        c.setWaiting(v);
        tt.setWaiting(v);
      },
      setConnecting: (v: boolean): void => {
        c.setConnecting(v);
        tt.setConnecting(v);
      },
      setOffline: (v: boolean): void => {
        c.setOffline(v);
        tt.setOffline(v);
      },
      setServerFull: (ms: number | null): void => c.setServerFull(ms),
      setWordmark: (img): void => {
        c.setWordmark(img);
        tt.setWordmark(img);
      },
      setSourceUrl: (url: string): void => c.setSourceUrl(url),
      setOnline: (n: number | null): void => {
        c.setOnline(n);
        tt.setOnline(n);
      },
      setMoneyNote: (text: string | null, test: boolean): void => c.setMoneyNote(text, test),
      flushNick: (): void => {
        c.flushNick();
        tt.flushNick();
      },
    };
  }

  /** Switches the menu front; the classic front leaves the territory rooms (no life running). */
  private setFront(front: 'territory' | 'classic'): void {
    if (this.front === front) return;
    const nick = this.home.nick();
    this.front = front;
    this.territoryHome.setNick(nick);
    this.classicHome.setNick?.(nick);
    this.pendingTerritoryPlay = null;
    this.moneyPanelOpen = false;
    if (front === 'classic' && !this.lifeActive) {
      if (this.money.get().step !== MONEY_FLOW.OFF) {
        this.leaveMoney();
      } else if (this.practice.get().step !== PRACTICE_FLOW.OFF || this.store.isMoneyRoom) {
        this.practiceOff();
        this.backToFreeGame();
      }
    }
    // Back on the territory front from a cell room: sit in the practice arena again (SF-1).
    if (
      front === 'territory' &&
      !this.lifeActive &&
      !this.store.isMoneyRoom &&
      this.money.get().step === MONEY_FLOW.OFF &&
      this.territoryRoomFirst('')
    ) {
      this.openPractice();
    }
    this.applyFront();
    const s = this.state.get();
    if (s === APP_STATE.HOME || s === APP_STATE.BOOT) this.home.show();
  }

  /** The menu and HUD layers' front / sim attributes, the money panel. */
  private applyFront(): void {
    const { menuRoot, hudRoot } = this.opts;
    if (!this.territoryHome) return;
    menuRoot.dataset['front'] = this.front;
    menuRoot.classList.toggle('has-arena-results', this.arenaResults.visible);
    menuRoot.classList.toggle('in-classic-life', this.lifeActive && !this.arena);
    hudRoot.dataset['sim'] = this.arena ? 'arena' : 'cells';
    // Arena rooms: toasts move to the top centre, clear of the land pill (UI §2.1).
    this.opts.toastRoot.classList.toggle('is-arena', this.arena !== null);
    const moneyOpen = this.moneyPanelOpen || this.money.get().step !== MONEY_FLOW.OFF;
    this.territoryHome.setMoneyPanel(moneyOpen ? this.moneyCard.el : null);
    this.moneyCard.setActive(moneyOpen && this.front === 'territory');
  }

  /** Territory home ▸ Play: the arena (practice) room, joined first when needed. */
  private onTerritoryPlay(nick: string): void {
    if (this.demo) {
      this.playClick('play');
      this.demoPlay(nick);
      return;
    }
    if (this.lifeActive) {
      this.onPlayPressed(nick);
      return;
    }
    const moneyRoom = this.store.isMoneyRoom && !this.store.isPracticeRoom;
    if ((moneyRoom && this.money.get().step !== MONEY_FLOW.OFF) || this.store.isPracticeRoom) {
      this.onPlayPressed(nick);
      return;
    }
    this.playClick('play');
    if (this.info?.practice && !this.info.practice.enabled) {
      toast(t('practice.closed'), TOAST_MS, 'warning');
      return;
    }
    this.pendingTerritoryPlay = nick;
    this.territoryHome.setWaiting(true);
    this.openPractice();
  }

  /** Territory home ▸ Play for money: the money card in the home's panel (toggles). */
  private onMoneyDoor(): void {
    if (this.money.get().step !== MONEY_FLOW.OFF) {
      this.moneyPanelOpen = true;
    } else {
      this.moneyPanelOpen = !this.moneyPanelOpen;
      if (this.moneyPanelOpen) void this.openMoney().then(() => this.applyFront());
    }
    this.applyFront();
  }

  /** Arena results ▸ Play again (practice). */
  private onArenaAgain(): void {
    this.onContinue();
    this.onTerritoryPlay(this.home.nick());
  }

  /** WELCOME: builds the arena client for an arena room, drops it otherwise. */
  private setupArena(w: WelcomeMessage): void {
    if (this.demo) return;
    this.cancelArenaEnd();
    if (this.arena) {
      this.arena.destroy();
      this.arena = null;
    }
    if (w.arena) {
      this.arena = new ArenaClient(w.arena, w.tickRate, this.arenaDeps());
      this.territoryHome.setNote(null);
    }
    this.applyFront();
  }

  private arenaDeps(): ConstructorParameters<typeof ArenaClient>[2] {
    const { window: win, canvas, hudRoot } = this.opts;
    return {
      window: win,
      canvas,
      hudRoot,
      sound: this.sfx,
      settings: () => this.settings.get(),
      formatValue: (v) => (this.store.isMoneyRoom ? this.moneyLabel(v) : null),
      formatBoard: (amount, kind) => this.boardAmount(amount, kind),
      room: () => ({
        money: this.store.isMoneyRoom,
        practice: this.store.isPracticeRoom,
        cashOutKey: this.store.money.room?.cashOutKey ?? 'Q',
        cashOutHoldSec: this.store.money.room?.cashOutHoldSec || MONEY_DEFAULTS.cashOutHoldSec,
      }),
      sendHeading: (steps, seq) => {
        if (this.demo) this.demo.steer(steps);
        else this.socket.send(encodeArenaInput(steps, seq));
      },
      setCashOutHeld: (down) => this.setCashOutHeld(down),
      cashOutHeld: () => this.cashOutHeld,
      touch: () => this.isTouchDevice(),
      ownName: () => this.lifeNick ?? finalNick(this.home.nick()),
    };
  }

  /** A board amount as compact text ("$1.2k"), or null for score boards (UI §2.2). */
  private boardAmount(amount: number, kind: AmountKind | undefined): string | null {
    if (kind !== AMOUNT_KIND.LAMPORTS) return null;
    const currency = resolveLabelCurrency(this.settings.get().labelCurrency, this.store.money.room);
    const solUsd = this.store.money.solUsd;
    if (currency === 'USD' && solUsd > 0) return compactUsd(lamportsToUsd(amount, solUsd));
    return formatLeaderboardAmount(amount, kind, currency, solUsd);
  }

  /** The best max-land % before the life that just ended (the results' "new best"). */
  private arenaPrevBest: number | null = null;

  /**
   * An arena life ended (DEATH or CASH_OUT_DONE): the break-up and the pan to the killer play at
   * once; the results open after 1000 ms (own fault) / 2000 ms (killed) / 600 ms (cash-out,
   * forced exit) — at once when the tab is hidden (no replay of the death sequence, C-23).
   */
  private onArenaLifeEnd(kind: 'death' | 'cashOut', msg: DeathMessage | CashOutDoneMessage): void {
    const arena = this.arena;
    if (!arena) return;
    const now = performance.now();
    const info = msg.arena ?? null;
    arena.lifeEnded(kind, kind === 'death' ? (info?.killerPlayerId ?? 0) : 0, now);
    const room: ArenaRoomKind = arena.roomKind();
    this.arenaPrevBest = loadArenaBest(room);
    saveArenaBest(room, Math.max(info?.maxLandPct ?? 0, arena.store.own.maxLandPct));
    this.cashOutHeld = false;
    this.endLife();
    const hidden = attempt(() => this.opts.window.document.hidden, false);
    const delay = hidden
      ? 0
      : resultsDelayMs(kind, info?.cause ?? 0, {
          self: ARENA_CLIENT.resultsDelaySelfMs,
          killed: ARENA_CLIENT.resultsDelayKilledMs,
          exit: ARENA_CLIENT.resultsDelayExitMs,
        });
    this.cancelArenaEnd();
    this.arenaEndTimer = setTimeout(() => {
      this.arenaEndTimer = null;
      if (this.stopped) return;
      if (kind === 'death') this.applyDeath(msg as DeathMessage);
      else this.finishArenaCashOut(msg as CashOutDoneMessage);
    }, delay);
  }

  /** An arena life is on screen from its first frame: no first-cell wait, the dim clears now. */
  private onArenaSpawned(playerId: number): void {
    const now = performance.now();
    this.arena?.spawned(playerId, now);
    this.firstCellSeen = true;
    // SF-2: the spawn is an instant cut to the play view (no dim, no fade).
    this.setDim(0, 0, now);
    this.worldFadeAtMs = null;
  }

  private cancelArenaEnd(): void {
    if (this.arenaEndTimer !== null) clearTimeout(this.arenaEndTimer);
    this.arenaEndTimer = null;
  }

  private finishArenaCashOut(msg: CashOutDoneMessage): void {
    this.moneyFlow({ type: 'lifeEnded' });
    this.dispatch({ type: 'cashOutDone' });
    if (this.state.get() !== APP_STATE.CASHED_OUT) return;
    this.payoutDelayedEarly = false;
    this.showArenaResults('cashOut', msg.arena ?? null, msg.value, msg.stats.timeAliveSec);
  }

  private showArenaResults(
    kind: 'death' | 'cashOut',
    info: DeathMessage['arena'] | null,
    value: number | null,
    timeAliveSec: number,
  ): void {
    const view = arenaResultView({
      kind,
      info: info ?? null,
      value,
      valueText: value !== null && this.store.isMoneyRoom ? this.moneyLabel(value) : null,
      timeAliveSec,
      previousBestPct: this.arenaPrevBest,
    });
    const now = performance.now();
    this.arenaResults.show(view, this.store.isPracticeRoom || this.demo !== null, now);
    // SF-3: the results backdrop is the live world, blanked and fading back in over 2 s while
    // the camera pans to the map centre and zooms out to the spectator view (no extra dim).
    this.arena?.spectate();
    this.worldFadeAtMs = now;
    this.setDim(0, 0, now);
    this.applyFront();
  }

  /**
   * One display frame while the arena (or the territory home's backdrop) is what the canvas
   * shows. Returns false when the cell renderer draws instead.
   */
  private arenaFrame(now: number, dt: number, s: AppState): boolean {
    const arena = this.arena;
    const home = s === APP_STATE.BOOT || s === APP_STATE.HOME;
    const backdrop = !arena && !this.lifeActive && this.front === 'territory' && home;
    if (!arena && !backdrop) return false;
    const canvas = this.opts.canvas;
    try {
      this.syncSize();
      const ctx = get2d(canvas);
      const k = canvas.width / Math.max(1, this.viewW);
      if (arena) {
        const rt = this.demo ? this.stepDemo(now, dt) : this.clock.renderTick(now);
        arena.frame(now, dt, {
          playing: s === APP_STATE.PLAYING,
          visible: true,
          widthPx: this.viewW,
          heightPx: this.viewH,
          k,
          dpr: this.dpr,
          renderTick: rt,
          rttMs: this.demo ? 0 : this.socket.rttMs,
        });
      } else if (ctx) {
        this.backdrop ??= new ArenaBackdrop();
        const prefs = this.settings.get();
        this.backdrop.frame(ctx, now, dt, this.viewW, this.viewH, k, {
          hideNames: prefs.hideNames,
          hideSkins: prefs.hideSkins,
        });
      }
      if (ctx) {
        // The territory home's first show fades the world in (SF-5), as do the results (SF-3).
        if (this.front === 'territory' && home && !this.homeFaded) {
          this.homeFaded = true;
          this.worldFadeAtMs = now;
        }
        this.drawWorldFade(ctx, now, canvas.width, canvas.height);
        // The dim (visuals §11). The territory home darkens with its own 0.8 black layer
        // (styles/arena.css .t-home, SF-5), so the canvas adds none there.
        const d = this.arenaDim.step(dt);
        const a = this.front === 'territory' && home ? 0 : d;
        if (a > 0.001) {
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.globalAlpha = a;
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.globalAlpha = 1;
        }
      }
    } catch (err) {
      this.report(err);
    }
    this.arenaDrawn = true;
    if (isMenuOpen(s)) this.territoryHome.tick(now);
    this.arenaResults.update(now);
    try {
      this.updateHud(now, s);
    } catch (err) {
      this.report(err);
    }
    return true;
  }

  /**
   * The world fade (SF-3, SF-5): the reference fades its canvas in from the page colour over 2 s
   * on the CSS `ease` curve; we paint the ground colour over the drawn world at 1 − ease(t).
   */
  private drawWorldFade(ctx: Ctx2D, now: number, w: number, h: number): void {
    const at = this.worldFadeAtMs;
    if (at === null) return;
    const t = (now - at) / WORLD_FADE_MS;
    if (t >= 1) {
      this.worldFadeAtMs = null;
      return;
    }
    const a = 1 - cssEase(Math.max(0, t));
    if (a <= 0.001) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = a;
    ctx.fillStyle = css(ARENA_CLIENT.colors.ground);
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;
  }

  // --- dev: `?arenaDemo` (the arena client against the client-only demo arena) ---------------

  /** Dev builds: `?arenaDemo` plays the arena client against demo.ts (no server room). */
  private startArenaDemo(): void {
    const demo = new DemoArena({
      player: true,
      size: 200,
      bots: 4,
      seed: (Date.now() & 0xffffff) | 1,
    });
    this.demo = demo;
    this.arena = new ArenaClient(demo.info, demo.tickRate, this.arenaDeps());
    for (let i = 0; i < 25 * 20; i++) this.arena.applyFrame(demo.step(), performance.now());
    this.arena.store.flushDeferred();
    this.arena.store.takeEvents();
    this.applyFront();
  }

  /** Steps the demo at 25 Hz; returns the fractional render tick. */
  private stepDemo(now: number, dt: number): number {
    const demo = this.demo;
    const arena = this.arena;
    if (!demo || !arena) return 0;
    this.demoAccMs += Math.min(dt, 200);
    while (this.demoAccMs >= 40) {
      this.demoAccMs -= 40;
      const f: FrameMessage = demo.step();
      const ownDeath = f.arenaEvents?.find(
        (e) => e.type === ARENA_EVENT.DEATH && e.victimId === demo.playerId,
      );
      arena.applyFrame(f, now);
      if (ownDeath && ownDeath.type === ARENA_EVENT.DEATH && this.demoAlive) {
        this.demoAlive = false;
        const msg: DeathMessage = {
          op: S2C.DEATH,
          pelletsEaten: 0,
          highestMass: 0,
          timeAliveSec: arena.lifeMs(now) / 1000,
          boardTimeSec: 0,
          ballsEaten: 0,
          bestRank: 0,
          sampleIntervalSec: 1,
          samples: [],
          arena: {
            cause: ownDeath.cause,
            atWall: false,
            forcedExit: false,
            killerPlayerId: ownDeath.killerId,
            killerName: arena.store.nameOf(ownDeath.killerId),
            landPct: 0,
            maxLandPct: arena.store.own.maxLandPct,
            kills: arena.store.own.kills,
            captures: 0,
          },
        };
        this.onArenaLifeEnd('death', msg);
      }
    }
    return arena.store.lastTick - 1 + this.demoAccMs / 40;
  }

  private demoPlay(nick: string): void {
    const demo = this.demo;
    const arena = this.arena;
    if (!demo || !arena) return;
    if (this.lifeActive) {
      if (this.state.get() === APP_STATE.HOME) this.dispatch({ type: 'resume' });
      return;
    }
    demo.respawnPlayer(finalNick(nick), this.settings.get().arenaSkin);
    this.lifeActive = true;
    this.lifeNick = finalNick(nick);
    this.demoAlive = true;
    this.home.setAlive(true);
    this.onArenaSpawned(demo.playerId);
    this.dispatch({ type: 'playing' });
  }

  /** Each distinct runtime error is logged once (the loop keeps running). */
  private report(err: unknown): void {
    const key = err instanceof Error ? err.message : String(err);
    if (this.reported.has(key)) return;
    this.reported.add(key);
    console.error('[ball.io]', err);
  }
}
